/* eslint-disable react/prop-types */
import React from 'react';
import {
  Box,
  Card,
  Stack,
  Tooltip,
  Typography,
  Menu,
  Badge,
  TextField,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Cell,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_VisibilityState,
} from 'material-react-table';
import { ProjectLineItemDTO, ProjectLineItemsAndSummary } from './project.type';
import { calculateTotalCompletedStored } from './project.util';
import NumericFormatInput from '../general/NumericFormatInput';
import { toast } from 'react-toastify';
import { differsWithBuffer, formatCurrency, formatPercentage } from '../../common/utils/common.util';
import { NEGATIVE_NUMBER_COLOR, SUBTOTAL_ROW_BACKGROUND_COLOR } from '../../common/constants/general';
import {
  Delete,
  Edit,
  List,
  Help,
  Add,
  Fullscreen,
  FullscreenExit,
  Search,
  Undo,
  Functions,
  Check,
  Close,
  InfoOutlined,
  AutoAwesome,
} from '@mui/icons-material';
import { ViewColumnOutlined as ViewColumnIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EditPayappDrawlineitemDialog from './project-draws/EditPayappDrawlineitemDialog';
import { CreateDrawLineItemDTO } from './project-draws/project-draws.type';
import { usePrompt } from '../AppPrompt';
import WhiteFab from '../common/WhiteFab';
import {
  computeSubtotalDisplayData,
  computeSubtotalReanchors,
  useSubtotalActions,
  useSubtotalRangeEditor,
  SubtotalRangeApplyResult,
} from './subtotal.util';
import { compareArrays } from '../report/report.util';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import { Division, LineItemLabelEnum, LineItemUnitEnum } from '../glossary/glossary.type';
import { REPORT_COLORS } from '../report/constants';
import LegendItem from '../general/Legend';
import { useMRTStyling } from '../../hooks/useMRTStyling';
import { useExcelEditing, excelSelectedCellSx } from '../../hooks/useExcelEditing';
import { buildSelectAllCheckboxProps } from '../common/mrtSelectAllHelpers';
import {
  handleMergeFuzzyMatch,
  undoMergeFuzzyMatch,
  payappProcessTooltips,
  autoMergeFuzzyMatch,
  rematchByDescription,
  rematchByBudget,
  rematchByPreviousApplication,
  getAiMatchRequest,
  applyAiMatches,
} from '../file-upload/Payapp.util';
import { matchPayappLineItems } from '../../common/utils/openai/openai.api';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { GridFilterModel, GridLogicOperator, GridColDef } from '@mui/x-data-grid';
import { getStringOperators, getNumericOperators } from '../../common/utils/datagrid-sieve.util';
import FilterPanel from '../filter-panel/FilterPanel';
import { getAppliedFiltersCount } from '../filter-panel/filter-panel.util';
import { IconOverTextButton } from '../common/IconOverTextButton';
import { ButtonDivider } from '../common/ButtonDivider';
import { CustomManageColumnsMenu } from '../common/CustomManageColumnsMenu';
import { useDrag, useDragDropManager, useDrop } from 'react-dnd';
import TruncatedCell from '../common/TruncatedCell';

const DEFAULT_PAGE_SIZE = 100;

type MatchMode = 'description' | 'budget' | 'previousApplication';

const DRAW_LINE_ITEM_ROW_DRAG_TYPE = 'DRAW_LINE_ITEM_ROW';

interface DrawLineItemsTableProps {
  projectDraw: ProjectLineItemsAndSummary;
  filteredLineItems: ProjectLineItemDTO[];
  totalsLineItem: ProjectLineItemDTO;
  setProjectDraw: React.Dispatch<React.SetStateAction<ProjectLineItemsAndSummary>>;
  drawInputRetainagePercentage?: number;
  divisionsData: Division[];
  readonly?: boolean;
  /** When true, suppresses discrepancy highlights for completeThisPeriod and storedThisPeriod when (project + payapp) equals the system value */
  isSecondPayappFlow?: boolean;
  /** Set of column header names assigned in step 2; used so merge uses project value when a column was not assigned */
  assignedPayappColumns?: Set<string>;
  /** When true, suppresses red-box alerts for previousApplication, completeThisPeriod, storedThisPeriod and completedStoredToDate (skipped payapp handling) */
  handleSkippedPayapp?: boolean;
  /** Called after a manual merge completes (fuzzy-button or checkbox-based). Used by callers for analytics. */
  /** `source` is 'selected' when the user paired two rows themselves (Merge Selected) rather than accepting a suggestion. */
  onMerge?: (payappItem: ProjectLineItemDTO, projectItem?: ProjectLineItemDTO, source?: 'selected') => void;
  /** When true (manual-update payapp flow), typed Original Budget/Previous Application values are stored on payappRow only, not the system value */
  isManualUpdate?: boolean;
  /** When true (payapp step 3), View Unmerged starts on */
  isMergeStep?: boolean;
}

type DrawLineItemDragItem = {
  draggedRowId: string;
  lastHoverRowId: string;
};

function DraggableDescriptionCell({
  rowId,
  disabled,
  moveRow,
  children,
}: {
  rowId: string;
  disabled: boolean;
  moveRow: (dragRowId: string, hoverRowId: string) => void;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef] = useDrag({
    type: DRAW_LINE_ITEM_ROW_DRAG_TYPE,
    canDrag: !disabled,
    item: { draggedRowId: rowId, lastHoverRowId: rowId } as DrawLineItemDragItem,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, dropRef] = useDrop({
    accept: DRAW_LINE_ITEM_ROW_DRAG_TYPE,
    hover: () => {
      // Intentionally no-op: only reorder on drop for smoother scrolling.
    },
    drop: (item: DrawLineItemDragItem) => {
      if (disabled) return;
      if (!ref.current) return;
      if (item.draggedRowId === rowId) return;

      moveRow(item.draggedRowId, rowId);
    },
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  dragRef(dropRef(ref));

  return (
    <Box
      ref={ref}
      sx={{
        cursor: disabled ? 'default' : 'move',
        opacity: isDragging ? 0.5 : 1,
        borderRadius: 1,
        width: '100%',
        minWidth: 0,
        outline: isOver && !disabled ? theme => `2px solid ${theme.palette.primary.main}` : 'none',
        outlineOffset: '2px',
      }}
      title={disabled ? undefined : 'Drag to reorder'}
    >
      {children}
    </Box>
  );
}

DraggableDescriptionCell.displayName = 'DraggableDescriptionCell';

const renderColCell = (
  highlightMismatch = true,
  drawInputRetainagePercentage?: number,
  isSecondPayappFlow?: boolean,
  handleSkippedPayapp?: boolean,
  isManualUpdate?: boolean,
) => {
  // eslint-disable-next-line react/prop-types
  const CellRenderer = ({ cell, row }: { cell: MRT_Cell<ProjectLineItemDTO>; row: MRT_Row<ProjectLineItemDTO> }) => {
    let value = cell.getValue<number>();
    // Ensure value is a valid number
    value = typeof value === 'string' ? parseFloat(value) : value;
    value = isNaN(value as number) ? 0 : value;

    const isPercentageCompleteColumn = cell.column.id === 'percentageComplete';
    const isTotalRow = row.id === 'totals';
    const alwaysTwoDecimals = cell.column.id === 'reallocation';

    let textColor = 'inherit';
    let fontWeight: React.CSSProperties['fontWeight'] = 'normal';

    if (value < 0 && !isTotalRow) {
      textColor = NEGATIVE_NUMBER_COLOR;
    }

    if (highlightMismatch && cell.column.id === 'retainage') {
      const completedStoredToDate = Number(row.getValue('completedStoredToDate')) || 0; //change 1

      const expectedRetainage = Math.round((completedStoredToDate * (drawInputRetainagePercentage || 0)) / 100);
      const valueDiffersFromExpected = value != null && Math.round(value) !== expectedRetainage;

      if (valueDiffersFromExpected || row.original.manualOverrides?.retainage) {
        fontWeight = 'bold';
      }
    }

    if (isPercentageCompleteColumn) {
      if (isTotalRow) return <div></div>;
      return (
        <div style={{ color: textColor, fontWeight, textAlign: 'right', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
          {formatPercentage(value?.toString())}
        </div>
      );
    } else if (row.original.payappRow) {
      const payappValue = row.original.payappRow[cell.column.id as keyof typeof row.original.payappRow];
      const projectValue = row.original.projectRow?.[cell.column.id as keyof typeof row.original.projectRow];
      let systemValue = value;

      // Ensure systemValue is a valid number before calling toFixed
      if (systemValue !== null && systemValue !== undefined && !isNaN(Number(systemValue))) {
        systemValue = +Number(systemValue).toFixed(2);
      } else {
        systemValue = 0;
      }

      const mergedRowsMonitoredColumns = ['originalBudget', 'reallocation', 'changeOrder', 'revisedBudget'];
      const payappOnlyMonitoredColumns = ['previousApplication', 'completedStoredToDate', 'balanceToComplete']; //mergedrow check payapp and system?

      const isMergedRowsMonitoredColumn = cell.column.id && mergedRowsMonitoredColumns.includes(cell.column.id);

      // For MergedRows monitored columns, show project value if it differs from either system or payapp value
      const projectValueDiffersFromPayappOrSystem =
        isMergedRowsMonitoredColumn &&
        projectValue !== undefined &&
        (payappValue !== undefined || systemValue !== undefined) &&
        (differsWithBuffer(projectValue, systemValue) || differsWithBuffer(projectValue, payappValue));

      const payappValueDiffersFromSystem = // payapp vs system always checked for merged rows (for all cols), for payapponly rows (check only payappMonitoredCols)
        payappValue !== undefined && systemValue !== undefined && differsWithBuffer(payappValue, systemValue);

      const projectRetainageDiffersFromSystem =
        cell.column.id === 'retainage' &&
        projectValue !== undefined &&
        systemValue !== undefined &&
        differsWithBuffer(projectValue, systemValue);

      // In payapp-2 workflow, suppress discrepancy highlights for columns where
      // the system value is intentionally derived from payapp-1 (project) data.
      //
      // completeThisPeriod / storedThisPeriod: system = CT1+CT2 (project+payapp).
      // Suppress when (project + payapp) == system.
      const isSecondPayappSumColumn =
        isSecondPayappFlow &&
        (cell.column.id === 'completeThisPeriod' || cell.column.id === 'storedThisPeriod') &&
        projectValue !== undefined &&
        payappValue !== undefined &&
        !differsWithBuffer(Number(projectValue ?? 0) + Number(payappValue ?? 0), systemValue ?? 0);

      // previousApplication: system is set to payapp-1 (project) value by design.
      // Suppress when system == project (payapp-1 value), regardless of what payapp-2 reports.
      const isSecondPayappPreviousApp =
        isSecondPayappFlow &&
        cell.column.id === 'previousApplication' &&
        projectValue !== undefined &&
        !differsWithBuffer(Number(projectValue ?? 0), systemValue ?? 0);

      // Skipped-payapp suppression: when the user opted to handle a skipped payapp,
      // remove red-box alerts from the columns that differ solely because of the skip.
      const isSkippedPayappSuppressedColumn =
        !!handleSkippedPayapp &&
        (cell.column.id === 'previousApplication' ||
          cell.column.id === 'completeThisPeriod' ||
          cell.column.id === 'storedThisPeriod' ||
          cell.column.id === 'completedStoredToDate');

      // Manual-update flow: a typed previousApplication becomes both the system and the payapp value, so
      // the discrepancy is flagged against the CDCC-calculated value carried on projectRow instead.
      const isManualPreviousAppOverridden =
        !!isManualUpdate && cell.column.id === 'previousApplication' && differsWithBuffer(projectValue, systemValue);

      const isMismatch =
        !(isSecondPayappSumColumn || isSecondPayappPreviousApp || isSkippedPayappSuppressedColumn) &&
        (payappValueDiffersFromSystem || projectValueDiffersFromPayappOrSystem || isManualPreviousAppOverridden);

      // Row-level discrepancy for payapp-only rows
      const monitoredRowColumns = ['reallocation', 'completedStoredToDate', 'balanceToComplete'];

      const getSystemValue = (field: string) => {
        switch (field) {
          case 'reallocation':
            return row.original.reallocation;
          case 'completedStoredToDate': {
            return (row.original.previousApplication || 0) + (row.original.cdccApproved || 0);
          }
          case 'balanceToComplete': {
            const revisedBudget = (row.original.originalBudget || 0) + (row.original.reallocation || 0) + (row.original.changeOrder || 0);
            const completedStoredToDate = (row.original.previousApplication || 0) + (row.original.cdccApproved || 0);
            return revisedBudget - completedStoredToDate;
          }
          default:
            return undefined;
        }
      };

      // checks ['reallocation', 'completedStoredToDate', 'balanceToComplete'] regardless of current cell
      const rowHasMonitoredDiff = monitoredRowColumns.some(field => {
        const sysRaw = row.getValue(field);
        const payRaw = row.original?.payappRow?.[field as keyof typeof row.original.payappRow];

        const sys = typeof sysRaw === 'number' ? sysRaw : Number(sysRaw);
        const pay = typeof payRaw === 'number' ? payRaw : Number(payRaw);

        if (!Number.isFinite(sys) || !Number.isFinite(pay)) return false;

        return differsWithBuffer(Number(sys.toFixed(2)), Number(pay.toFixed(2)));
      });

      // System value is the source of truth — skipped-payapp columns have already had
      // payapp values baked in at the data level, so no display override is needed.
      const displayValue = systemValue;

      return (
        <div
          style={{
            border:
              isMismatch || projectValueDiffersFromPayappOrSystem
                ? `1.5px solid ${REPORT_COLORS.PAYAPP_PROJECT_MISMATCH_BORDER_COLOR}`
                : 'unset',
            backgroundColor:
              isMismatch || projectValueDiffersFromPayappOrSystem ? REPORT_COLORS.PAYAPP_PROJECT_MISMATCH_BACKGROUND_COLOR : 'inherit',
            padding: isMismatch || row.original?.projectRow ? '5px 15px' : '5px 1px 5px 15px',
            borderRadius: '5px',
            width: '100%',
            height: 'calc(100% - 10px)',
            margin: '5px 0',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack direction={'row'} gap={0.5} alignItems={'center'} justifyContent={'flex-end'} sx={{ width: '100%' }}>
            <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              <div
                style={{
                  color:
                    (payappValueDiffersFromSystem && !isSecondPayappSumColumn && !isSecondPayappPreviousApp) ||
                    (isManualPreviousAppOverridden && isMismatch)
                      ? REPORT_COLORS.PAYAPP_PROJECT_MISMATCH_BORDER_COLOR
                      : textColor,
                  fontWeight,
                }}
              >
                {formatCurrency(displayValue?.toString(), true, false, alwaysTwoDecimals)}
              </div>

              {(isMismatch || rowHasMonitoredDiff) && (
                <div style={{ color: REPORT_COLORS.ADJUSTMENT_COLOR, fontWeight, fontSize: '13px' }}>
                  {formatCurrency((payappValue ?? '--')?.toString(), true, false, alwaysTwoDecimals)}
                </div>
              )}

              {row.original?.projectRow && (isMismatch || rowHasMonitoredDiff || projectRetainageDiffersFromSystem) && (
                <div style={{ color: REPORT_COLORS.ADJUSTMENT_COLOR, fontWeight, fontSize: '13px' }}>
                  {formatCurrency((projectValue ?? '--')?.toString(), true, false, alwaysTwoDecimals)}
                </div>
              )}
            </div>
          </Stack>
        </div>
      );
    } else {
      return (
        <div
          style={{
            color: textColor,
            fontWeight,
            textAlign: 'right',
            width: '100%',
            fontVariantNumeric: 'tabular-nums',
            padding: '5px 15px',
            boxSizing: 'border-box',
          }}
        >
          {formatCurrency(value?.toString(), true, false, alwaysTwoDecimals)}
        </div>
      );
    }
  };

  CellRenderer.displayName = highlightMismatch ? 'InputRetMismatchCell' : 'DefaultNumberCell';

  return CellRenderer;
};

function DrawLineItemsTable({
  projectDraw,
  filteredLineItems,
  totalsLineItem,
  setProjectDraw,
  drawInputRetainagePercentage,
  divisionsData,
  readonly,
  isSecondPayappFlow,
  assignedPayappColumns,
  handleSkippedPayapp,
  onMerge,
  isManualUpdate,
  isMergeStep = false,
}: DrawLineItemsTableProps) {
  const apiRef = React.useRef<MRT_TableInstance<ProjectLineItemDTO> | null>(null);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const { getConfirmation } = usePrompt();
  const { handleDeleteSubtotalClick } = useSubtotalActions({
    drawId: projectDraw.projectDrawSummary?.id ?? '',
    subtotals: projectDraw.subtotals ?? [],
    onSubtotalsChange: next => setProjectDraw({ ...projectDraw, subtotals: next }),
    getConfirmation,
  });

  const handleRangeEditApply = React.useCallback(
    (result: SubtotalRangeApplyResult) => {
      const isPersisted = !result.subtotalId.startsWith('temp-subtotal-');
      const nextSubtotals = (projectDraw.subtotals ?? []).map(s =>
        s.id === result.subtotalId
          ? { ...s, anchorDrawLineItemId: result.anchorDrawLineItemId, anchorSerialNumber: result.anchorSerialNumber, range: result.range }
          : s,
      );
      setProjectDraw({
        ...projectDraw,
        lineItems: result.reorderedLineItems,
        subtotals: nextSubtotals,
        // Already-persisted subtotals get their anchor/range change flushed on the next Save, via
        // the same pendingSubtotalReanchors mechanism used when a line item anchoring a subtotal
        // is deleted — NOT sent immediately. It's coupled to the line item reorder above, which
        // itself only persists on Save (isEdited-flagged), so an immediate call here would let the
        // two halves of this change fall out of sync if the user cancels/navigates away before
        // saving. Not-yet-saved subtotals (temp-subtotal- ids) don't need this at all — their
        // anchor/range is read fresh from `subtotals` at save time by ProjectWrapper.tsx's
        // createSubtotals mapping.
        pendingSubtotalReanchors: isPersisted
          ? [
              ...(projectDraw.pendingSubtotalReanchors ?? []).filter(r => r.subtotalId !== result.subtotalId),
              { subtotalId: result.subtotalId, newAnchorDrawLineItemId: result.anchorDrawLineItemId, range: result.range },
            ]
          : projectDraw.pendingSubtotalReanchors,
      });
    },
    [projectDraw, setProjectDraw],
  );

  const rangeEditor = useSubtotalRangeEditor({
    lineItems: projectDraw.lineItems ?? [],
    subtotals: projectDraw.subtotals ?? [],
    onApply: handleRangeEditApply,
  });

  const [selectedLineItem, setSelectedLineItem] = React.useState<ProjectLineItemDTO | null>(null);
  const [editDrawLineItemDialogOpen, setEditDrawLineItemDialogOpen] = React.useState(false);
  const [selectedLineItemsIds, setSelectedLineItemsIds] = React.useState<string[]>([]);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = React.useState(false);
  const [addingLineItem, setAddingLineItem] = React.useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<MRT_VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});

  // While a subtotal is in range-edit mode, the grid's checkbox column is repurposed to show/
  // toggle that subtotal's claimed line items instead of the normal bulk-selection state.
  const rangeEditSelectionState = React.useMemo(() => {
    const state: MRT_RowSelectionState = {};
    rangeEditor.checkedIds.forEach(id => {
      state[id] = true;
    });
    return state;
  }, [rangeEditor.checkedIds]);

  const handleRowSelectionChange = React.useCallback(
    (updaterOrValue: MRT_RowSelectionState | ((old: MRT_RowSelectionState) => MRT_RowSelectionState)) => {
      if (rangeEditor.rangeEditSubtotalId) {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(rangeEditSelectionState) : updaterOrValue;
        const prevKeys = new Set(Object.keys(rangeEditSelectionState).filter(k => rangeEditSelectionState[k]));
        const nextKeys = new Set(Object.keys(next).filter(k => next[k]));
        for (const id of Array.from(nextKeys)) {
          if (!prevKeys.has(id)) {
            rangeEditor.toggleItem(id, true);
            return;
          }
        }
        for (const id of Array.from(prevKeys)) {
          if (!nextKeys.has(id)) {
            rangeEditor.toggleItem(id, false);
            return;
          }
        }
        return;
      }
      setRowSelection(updaterOrValue);
    },
    [rangeEditor, rangeEditSelectionState],
  );

  const [sorting, setSorting] = React.useState<any>([]);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<HTMLElement | null>(null);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({ items: [], logicOperator: GridLogicOperator.And });
  const [showUnmergedOnly, setShowUnmergedOnly] = React.useState(isMergeStep);
  // Steps 3 and 4 share this table instance, so reset when the step changes
  React.useEffect(() => {
    setShowUnmergedOnly(isMergeStep);
  }, [isMergeStep]);
  const [matchMode, setMatchMode] = React.useState<MatchMode>('description');
  // Local draft for the retainage % field (manual-update flow) so keystrokes don't push into shared state until blur.
  const [inputRetainagePercentageDraft, setInputRetainagePercentageDraft] = React.useState<number | string>(
    drawInputRetainagePercentage ?? 0,
  );
  const projectDrawId = projectDraw.projectDrawSummary?.id;
  React.useEffect(() => {
    setMatchMode('description');
  }, [projectDrawId]);
  React.useEffect(() => {
    setInputRetainagePercentageDraft(drawInputRetainagePercentage ?? 0);
  }, [drawInputRetainagePercentage]);
  const { getOnSortingChangeWithToast } = useMRTStyling<ProjectLineItemDTO>();
  const divisions = divisionsData || [];
  const isSorted = (sorting?.length ?? 0) > 0;
  const reorderDisabled = Boolean(readonly) || isSorted || !!rangeEditor.rangeEditSubtotalId;

  // Auto-scroll while dragging when the cursor nears the top/bottom edges.
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();
  React.useEffect(() => {
    let animationFrameId: number | null = null;
    let isScrolling = false;
    const windowScrollThreshold = 120; // px from top/bottom of viewport
    const containerScrollThreshold = 90; // px from top/bottom of container
    const baseScrollStep = 8;
    const maxMultiplier = 4;

    const scrollLoop = () => {
      if (!monitor.isDragging() || monitor.getItemType() !== DRAW_LINE_ITEM_ROW_DRAG_TYPE) {
        isScrolling = false;
        return;
      }

      const offset = monitor.getClientOffset();
      if (!offset) {
        isScrolling = false;
        return;
      }

      const { y } = offset;
      let shouldContinueScrolling = false;

      const container = tableContainerRef.current;

      // Window scrolling near viewport edges
      const viewportHeight = window.innerHeight;
      const distanceFromTop = y;
      const distanceFromBottom = viewportHeight - y;
      if (distanceFromTop < windowScrollThreshold) {
        const canScrollWindowUp = window.scrollY > 0;
        if (canScrollWindowUp) {
          const proximity = 1 - distanceFromTop / windowScrollThreshold;
          const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
          window.scrollBy({ top: -baseScrollStep * speedFactor, behavior: 'auto' });
          shouldContinueScrolling = true;
        }
      } else if (distanceFromBottom < windowScrollThreshold) {
        const canScrollWindowDown = window.scrollY + viewportHeight < document.documentElement.scrollHeight;
        if (canScrollWindowDown) {
          const proximity = 1 - distanceFromBottom / windowScrollThreshold;
          const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
          window.scrollBy({ top: baseScrollStep * speedFactor, behavior: 'auto' });
          shouldContinueScrolling = true;
        }
      }

      // Container scrolling near container edges
      if (container) {
        const rect = container.getBoundingClientRect();
        const withinX = offset.x >= rect.left && offset.x <= rect.right;
        const withinYBand = y >= rect.top - containerScrollThreshold && y <= rect.bottom + containerScrollThreshold;
        if (withinX && withinYBand) {
          const distanceFromContainerTop = y - rect.top;
          const distanceFromContainerBottom = rect.bottom - y;
          if (distanceFromContainerTop < containerScrollThreshold && distanceFromContainerTop >= 0) {
            const canScrollUp = container.scrollTop > 0;
            if (canScrollUp) {
              const proximity = 1 - distanceFromContainerTop / containerScrollThreshold;
              const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
              container.scrollBy({ top: -baseScrollStep * speedFactor, behavior: 'auto' });
              shouldContinueScrolling = true;
            }
          } else if (distanceFromContainerBottom < containerScrollThreshold && distanceFromContainerBottom >= 0) {
            const canScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight;
            if (canScrollDown) {
              const proximity = 1 - distanceFromContainerBottom / containerScrollThreshold;
              const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
              container.scrollBy({ top: baseScrollStep * speedFactor, behavior: 'auto' });
              shouldContinueScrolling = true;
            }
          }
        }
      }

      if (shouldContinueScrolling) {
        isScrolling = true;
        animationFrameId = requestAnimationFrame(scrollLoop);
      } else {
        isScrolling = false;
      }
    };

    const handleOffsetChange = () => {
      if (monitor.isDragging() && !isScrolling) {
        scrollLoop();
      }
    };

    const unsubscribeOffset = monitor.subscribeToOffsetChange(handleOffsetChange);

    return () => {
      unsubscribeOffset();
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [monitor]);

  const moveRow = React.useCallback(
    (dragRowId: string, hoverRowId: string) => {
      if (reorderDisabled) return;
      if (!dragRowId || !hoverRowId || dragRowId === hoverRowId) return;

      const all = projectDraw.lineItems ?? [];
      if (!all.length) return;

      // Reorder only the non-deleted items, keeping deleted items in their original slots.
      const active = all.filter(li => !li.isDeleted);
      const dragIndex = active.findIndex(li => li.id === dragRowId);
      const hoverIndex = active.findIndex(li => li.id === hoverRowId);
      if (dragIndex < 0 || hoverIndex < 0 || dragIndex === hoverIndex) return;

      const nextActive = [...active];
      const [moved] = nextActive.splice(dragIndex, 1);
      nextActive.splice(hoverIndex, 0, moved);

      // Update serialNumber to reflect the new order (starting from 1)
      const nextActiveWithUpdatedSerialNumbers = nextActive.map((item, index) => ({
        ...item,
        serialNumber: index + 1,
        isEdited: !item.isCreated ? true : item.isEdited,
      }));

      let i = 0;
      const nextAll = all.map(li => (li.isDeleted ? li : nextActiveWithUpdatedSerialNumbers[i++]));

      setProjectDraw({
        ...projectDraw,
        lineItems: nextAll,
      });
    },
    [reorderDisabled, projectDraw, setProjectDraw],
  );

  // Define filterable columns
  const filterColumns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'lineItem.description',
        headerName: 'Description',
        type: 'string',
        filterOperators: getStringOperators(),
      },
      {
        field: 'lineItem.division.code',
        headerName: 'Division',
        type: 'string',
        filterOperators: getStringOperators(),
      },
      {
        field: 'originalBudget',
        headerName: 'Original Budget',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'reallocation',
        headerName: 'Reallocation',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'changeOrder',
        headerName: 'Change Order',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'revisedBudget',
        headerName: 'Revised Budget',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'previousApplication',
        headerName: 'Previous Application',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'completeThisPeriod',
        headerName: 'Complete This Period',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'storedThisPeriod',
        headerName: 'Stored This Period',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'completedStoredToDate',
        headerName: 'Completed Stored To Date',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'balanceToComplete',
        headerName: 'Balance To Complete',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
      {
        field: 'retainage',
        headerName: 'Retainage',
        type: 'number',
        filterOperators: getNumericOperators(),
      },
    ],
    [],
  );

  const skippedFields: string[] = [];

  // Add this check for any fuzzy-matched rows
  const hasFuzzyMatchedRows = React.useMemo(() => {
    return filteredLineItems.some(item => item.matchType === 'fuzzy' && item.isCreated);
  }, [filteredLineItems]);

  // Check for merged items that can be undone
  const hasMergedRows = React.useMemo(() => {
    return filteredLineItems.some(item => item.matchType === 'exact' && item.payappRow && item.projectRow);
  }, [filteredLineItems]);

  // Only filter while the View Unmerged switch is visible, so rows can't be hidden with no way to toggle back
  const isUnmergedFilterActive = showUnmergedOnly && (hasFuzzyMatchedRows || hasMergedRows);

  // Apply filters to data
  const filteredData = React.useMemo(() => {
    const baseItems = isUnmergedFilterActive
      ? filteredLineItems.filter(item => item.matchType === 'fuzzy' || item.matchType === 'none')
      : filteredLineItems;

    if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
      return baseItems;
    }

    return baseItems.filter((item: ProjectLineItemDTO) => {
      return filterModel.items.every(filterItem => {
        const { field, operator, value } = filterItem;
        if (!value && value !== 0) return true;

        // Get nested field value
        const getFieldValue = (obj: any, path: string) => {
          return path.split('.').reduce((curr, prop) => curr?.[prop], obj);
        };

        let fieldValue = getFieldValue(item, field);

        // For calculated fields
        if (field === 'revisedBudget') {
          fieldValue = (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0);
        } else if (field === 'completedStoredToDate') {
          fieldValue = (item.previousApplication || 0) + (item.cdccApproved || 0);
        } else if (field === 'balanceToComplete') {
          const revisedBudget = (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0);
          const completedStoredToDate = (item.previousApplication || 0) + (item.cdccApproved || 0);
          fieldValue = revisedBudget - completedStoredToDate;
        }

        if (fieldValue === undefined || fieldValue === null) return false;

        const fieldValueStr = String(fieldValue).toLowerCase();
        const valueStr = String(value).toLowerCase();

        switch (operator) {
          case 'contains':
            return fieldValueStr.includes(valueStr);
          case 'equals':
            return fieldValue === value || fieldValueStr === valueStr;
          case 'startsWith':
            return fieldValueStr.startsWith(valueStr);
          case 'endsWith':
            return fieldValueStr.endsWith(valueStr);
          case 'isEmpty':
            return !fieldValue || fieldValueStr === '';
          case 'isNotEmpty':
            return fieldValue && fieldValueStr !== '';
          case '>':
            return Number(fieldValue) > Number(value);
          case '>=':
            return Number(fieldValue) >= Number(value);
          case '<':
            return Number(fieldValue) < Number(value);
          case '<=':
            return Number(fieldValue) <= Number(value);
          case '=':
            return Number(fieldValue) === Number(value);
          case '!=':
            return Number(fieldValue) !== Number(value);
          default:
            return true;
        }
      });
    });
  }, [filteredLineItems, filterModel, isUnmergedFilterActive]);

  // While a subtotal is in range-edit mode, render the live in-progress reorder (see
  // rangeEditor.previewLineItems/previewSubtotals in subtotal.util.tsx) instead of the last-saved
  // order, so checking/unchecking a row visibly moves it right away rather than only on Confirm.
  const displayLineItems = React.useMemo(
    () => (rangeEditor.previewLineItems ? rangeEditor.previewLineItems.filter(item => !item.isDeleted) : filteredLineItems),
    [rangeEditor.previewLineItems, filteredLineItems],
  );
  const displayFilteredData = React.useMemo(() => {
    if (!rangeEditor.previewLineItems) return filteredData;
    const displayLineItemsById = new Map(displayLineItems.map(li => [li.id, li]));
    return filteredData.map(item => displayLineItemsById.get(item.id) ?? item);
  }, [filteredData, displayLineItems, rangeEditor.previewLineItems]);
  const displaySubtotals = rangeEditor.previewSubtotals ?? projectDraw.subtotals ?? [];
  const showSortedSubtotalsNotice = isSorted && displaySubtotals.some(s => !s.isDeleted);

  // Interleave subtotal rows among the filtered line items, sorted by position. A subtotal
  // renders immediately after the line item it's anchored to (using that anchor's *current*
  // serialNumber, so subtotals stay correctly placed across drag-reordering without any change
  // to moveRow itself). Subtotals are positional, so they're hidden while a column sort is active
  // and come back once the sort is cleared.
  const displayData = React.useMemo(
    () =>
      isSorted
        ? displayFilteredData
        : computeSubtotalDisplayData(
            displayFilteredData,
            displayLineItems,
            displaySubtotals,
            projectDraw.projectDrawSummary?.id ?? '',
            projectDraw.projectDrawSummary?.name ?? '',
          ),
    [isSorted, displayFilteredData, displayLineItems, displaySubtotals, projectDraw.projectDrawSummary],
  );

  // Function to check if selected items meet merge criteria
  const canMergeSelectedItems = React.useMemo(() => {
    const selectedRows = Object.keys(rowSelection);
    if (selectedRows.length !== 2) return false;

    const selectedItems = selectedRows.map(id => filteredData.find(item => item.id === id)).filter(Boolean) as ProjectLineItemDTO[];

    if (selectedItems.length !== 2) return false;

    // Check if one is payapp only and other is project only
    const hasPayappOnly = selectedItems.some(item => item.isCreated && !item.exitingAndNotMatched);
    const hasProjectOnly = selectedItems.some(item => !item.isCreated && item.exitingAndNotMatched);

    return hasPayappOnly && hasProjectOnly;
  }, [rowSelection, filteredData]);

  // Show match mode tabs only when there are project items to match against and unresolved rows exist.
  // On first payapp upload there are no project items, so matching is irrelevant.
  const hasUnresolvedRows = React.useMemo(() => {
    const hasProjectItems = filteredLineItems.some(item => item.exitingAndNotMatched || item.projectRow);
    return hasProjectItems && filteredLineItems.some(item => item.matchType === 'fuzzy' || item.matchType === 'none');
  }, [filteredLineItems]);

  const rematch = (draw: ProjectLineItemsAndSummary, mode: MatchMode) => {
    if (mode === 'budget') return rematchByBudget(draw, assignedPayappColumns);
    if (mode === 'previousApplication') return rematchByPreviousApplication(draw);
    return rematchByDescription(draw);
  };

  const handleMatchModeChange = (_: React.MouseEvent, newMode: MatchMode | null) => {
    if (!newMode || newMode === matchMode) return;
    const newDraw = rematch(projectDraw, newMode);
    if (newDraw === projectDraw) return;
    setProjectDraw(newDraw);
    setMatchMode(newMode);
  };

  const handleRowClick = (row: MRT_Row<ProjectLineItemDTO>) => {
    setSelectedLineItem(row.original);
  };

  const processRowUpdate = (updatedRow: ProjectLineItemDTO, oldRow: ProjectLineItemDTO) => {
    if (compareArrays(updatedRow, oldRow)) return oldRow;
    handleEditDrawLineItem(updatedRow);
    return updatedRow;
  };

  const handleEditDrawLineItem = async (updatedLineItem: ProjectLineItemDTO) => {
    try {
      // Find the original item to compare values
      const originalItem = projectDraw.lineItems?.find(item => item.id === updatedLineItem.id);
      const isProjectOnlyItem = originalItem?.exitingAndNotMatched === true;
      const isMergedItem = originalItem?.matchType === 'exact' && originalItem?.payappRow && originalItem?.projectRow;

      // Check if completeThisPeriod or storedThisPeriod changed
      const oldCompleteThisPeriod = originalItem?.completeThisPeriod ?? 0;
      const oldStoredThisPeriod = originalItem?.storedThisPeriod ?? 0;
      const newCompleteThisPeriod = updatedLineItem.completeThisPeriod ?? 0;
      const newStoredThisPeriod = updatedLineItem.storedThisPeriod ?? 0;

      const dependentFieldsChanged = oldCompleteThisPeriod !== newCompleteThisPeriod || oldStoredThisPeriod !== newStoredThisPeriod;

      // First sanitize the input values
      const sanitizedValues = {
        originalBudget: updatedLineItem.originalBudget ?? 0,
        reallocation: updatedLineItem.reallocation ?? 0,
        changeOrder: updatedLineItem.changeOrder ?? 0,
        previousApplication: updatedLineItem.previousApplication ?? 0,
        completeThisPeriod: newCompleteThisPeriod,
        storedThisPeriod: newStoredThisPeriod,
      };

      // Calculate derived values
      const revisedBudget = sanitizedValues.originalBudget + sanitizedValues.reallocation + sanitizedValues.changeOrder;

      // Compute cdccApproved first — completedStoredToDate depends on it
      let cdccApproved = updatedLineItem.cdccApproved ?? 0;
      if ((isProjectOnlyItem || isMergedItem) && dependentFieldsChanged) {
        cdccApproved = sanitizedValues.completeThisPeriod + sanitizedValues.storedThisPeriod;
      } else if (!isProjectOnlyItem && !isMergedItem && dependentFieldsChanged) {
        const oldExpectedCdccApproved = oldCompleteThisPeriod + oldStoredThisPeriod;
        const currentCdccApproved = originalItem?.cdccApproved ?? 0;
        const isManuallyOverridden = Math.abs(currentCdccApproved - oldExpectedCdccApproved) > 0.01;

        if (!isManuallyOverridden) {
          cdccApproved = sanitizedValues.completeThisPeriod + sanitizedValues.storedThisPeriod;
        }
      }

      const completedStoredToDate = sanitizedValues.previousApplication + cdccApproved;
      const percentageComplete = revisedBudget !== 0 ? (completedStoredToDate / revisedBudget) * 100 : 0;
      const balanceToComplete = revisedBudget - completedStoredToDate;

      let retainage = updatedLineItem.retainage;
      let manualOverrides = updatedLineItem.manualOverrides;
      const retainageChangedDirectly = (originalItem?.retainage ?? 0) !== (updatedLineItem.retainage ?? 0);
      const completedStoredBeforeEdit = calculateTotalCompletedStored(originalItem ?? updatedLineItem);
      // Only auto-calc (or infer an override) when an input to the retainage formula changed and a % is set.
      const retainageInputsChanged = completedStoredToDate !== completedStoredBeforeEdit;

      if (retainageChangedDirectly) {
        const expectedRetainage = Math.round(completedStoredToDate * ((drawInputRetainagePercentage || 0) / 100));
        manualOverrides = { ...manualOverrides, retainage: (retainage ?? 0) !== expectedRetainage };
      } else if (!originalItem?.manualOverrides?.retainage && retainageInputsChanged && drawInputRetainagePercentage) {
        const expectedRetainageBeforeEdit = Math.round(completedStoredBeforeEdit * (drawInputRetainagePercentage / 100));
        if (Math.round(originalItem?.retainage ?? 0) !== expectedRetainageBeforeEdit) {
          // Existing value doesn't match the expected calc — it was already overridden; stop auto-calculating it.
          manualOverrides = { ...manualOverrides, retainage: true };
        } else {
          retainage = Math.round(completedStoredToDate * (drawInputRetainagePercentage / 100));
        }
      }

      const payappRow =
        isManualUpdate && updatedLineItem.payappRow ? { ...updatedLineItem.payappRow, retainage } : updatedLineItem.payappRow;

      const sanitizedLineItem: ProjectLineItemDTO = {
        ...updatedLineItem,
        ...sanitizedValues,
        cdccApproved,
        revisedBudget,
        completedStoredToDate,
        percentageComplete,
        balanceToComplete,
        retainage,
        manualOverrides,
        payappRow,
        isDeleted: false,
        isCreated: updatedLineItem?.isCreated ? true : false,
        isEdited: !updatedLineItem?.isCreated,
      };

      setProjectDraw({
        ...projectDraw,
        lineItems: projectDraw.lineItems?.map(item => {
          if (item.id === updatedLineItem.id) {
            return {
              ...sanitizedLineItem,
              isDeleted: false,
              isCreated: updatedLineItem?.isCreated ? true : false,
              isEdited: !updatedLineItem?.isCreated,
            };
          }
          return item;
        }),
      });
    } catch (error) {
      console.error('Error updating line item:', error);
      toast.error('An error occurred while updating the line item.');
    }
  };

  // Excel editing hook - wrapper for handleEditDrawLineItem
  const handleSaveCell = (cell: MRT_Cell<ProjectLineItemDTO>, value: any) => {
    // Handle description (string) edits separately
    if (cell.column.id === 'lineItem.description') {
      const updatedLineItem: ProjectLineItemDTO = {
        ...cell.row.original,
        lineItem: {
          ...cell.row.original.lineItem,
          description: value ?? '',
        },
      };
      handleEditDrawLineItem(updatedLineItem);
      return;
    }

    // Default numeric handling for all other editable cells
    const numericValue = typeof value === 'string' && value.trim() === '' ? 0 : parseFloat(value);
    if (!isNaN(numericValue)) {
      const original = cell.row.original;
      // Flow-aware editing for payapp-only rows:
      // - Manual-update flow: the typed value IS the payapp value (payappRow), so it must be written there.
      //   For the columns below, top-level also stays locked to the pre-edit (payapp-1/baseline) value.
      //   previousApplication is not locked: the contractor's typed value replaces the system value too.
      // - Upload flow: the typed value IS the system value (top-level). payappRow holds the value captured
      //   in step 2 of the upload wizard and must never be overwritten by a later cell edit.
      const manualPayappLockedColumns = ['originalBudget'];
      const isLockedColumn = !!isManualUpdate && !!original.payappRow && manualPayappLockedColumns.includes(cell.column.id);
      const updatedPayappRow =
        isManualUpdate && original.payappRow ? { ...original.payappRow, [cell.column.id]: numericValue } : original.payappRow;
      if (isManualUpdate && updatedPayappRow) {
        // Keep payappRow.revisedBudget in sync when budget columns change
        if (cell.column.id === 'changeOrder' || cell.column.id === 'reallocation') {
          const payappOriginalBudget = Number(updatedPayappRow.originalBudget ?? 0);
          const payappReallocation = Number(cell.column.id === 'reallocation' ? numericValue : (updatedPayappRow.reallocation ?? 0));
          const payappChangeOrder = Number(cell.column.id === 'changeOrder' ? numericValue : (updatedPayappRow.changeOrder ?? 0));
          updatedPayappRow.revisedBudget = payappOriginalBudget + payappReallocation + payappChangeOrder;
        }
        // Keep payappRow.completedStoredToDate and balanceToComplete in sync when period columns change
        if (cell.column.id === 'previousApplication' || cell.column.id === 'completeThisPeriod' || cell.column.id === 'storedThisPeriod') {
          const payappPrevApp = Number(updatedPayappRow.previousApplication ?? 0);
          const payappComplete = Number(
            cell.column.id === 'completeThisPeriod' ? numericValue : (updatedPayappRow.completeThisPeriod ?? 0),
          );
          const payappStored = Number(cell.column.id === 'storedThisPeriod' ? numericValue : (updatedPayappRow.storedThisPeriod ?? 0));
          updatedPayappRow.completedStoredToDate = payappPrevApp + payappComplete + payappStored;
          updatedPayappRow.balanceToComplete = Number(updatedPayappRow.revisedBudget ?? 0) - updatedPayappRow.completedStoredToDate;
        }
        // Keep payappRow.balanceToComplete in sync when budget columns change
        if (cell.column.id === 'changeOrder' || cell.column.id === 'reallocation') {
          const payappPrevApp = Number(updatedPayappRow.previousApplication ?? 0);
          const payappComplete = Number(updatedPayappRow.completeThisPeriod ?? 0);
          const payappStored = Number(updatedPayappRow.storedThisPeriod ?? 0);
          const payappCompletedStoredToDate = payappPrevApp + payappComplete + payappStored;
          updatedPayappRow.completedStoredToDate = payappCompletedStoredToDate;
          updatedPayappRow.balanceToComplete = Number(updatedPayappRow.revisedBudget ?? 0) - payappCompletedStoredToDate;
        }
      }
      const updatedRow = {
        ...original,
        ...(!isLockedColumn && { [cell.column.id as keyof ProjectLineItemDTO]: numericValue as any }),
        ...(updatedPayappRow && { payappRow: updatedPayappRow }),
      };
      handleEditDrawLineItem(updatedRow);
    }
  };

  // Initialize Excel editing hook
  // Note: allFields will be updated after columns are defined, but hook works without it for validation
  const excel = useExcelEditing<ProjectLineItemDTO>({
    tableInstanceRef: apiRef,
    onSaveCell: handleSaveCell,
    excludedColumnIds: [
      'mrt-row-select',
      'mrt-row-actions',
      'division',
      'revisedBudget',
      'completedStoredToDate',
      'percentageComplete',
      'balanceToComplete',
    ],
    filteredRows: displayData,
    allFields: [
      'lineItem.description',
      'division',
      'originalBudget',
      'reallocation',
      'changeOrder',
      'revisedBudget',
      'previousApplication',
      'completeThisPeriod',
      'storedThisPeriod',
      'completedStoredToDate',
      'percentageComplete',
      'balanceToComplete',
      'retainage',
    ],
    containerRef: tableContainerRef,
  });

  const handleCreateOrEditLineItem = async (lineItem: CreateDrawLineItemDTO) => {
    try {
      if (selectedLineItem) {
        const originalItem = projectDraw.lineItems?.find(item => item.id === selectedLineItem.id);
        const isProjectOnlyItem = originalItem?.exitingAndNotMatched === true;
        const isMergedItem = originalItem?.matchType === 'exact' && originalItem?.payappRow && originalItem?.projectRow;

        // Check if completeThisPeriod or storedThisPeriod changed
        const oldCompleteThisPeriod = originalItem?.completeThisPeriod ?? 0;
        const oldStoredThisPeriod = originalItem?.storedThisPeriod ?? 0;
        const newCompleteThisPeriod = lineItem.completeThisPeriod ?? oldCompleteThisPeriod;
        const newStoredThisPeriod = lineItem.storedThisPeriod ?? oldStoredThisPeriod;

        const dependentFieldsChanged = oldCompleteThisPeriod !== newCompleteThisPeriod || oldStoredThisPeriod !== newStoredThisPeriod;

        // Calculate cdccApproved based on item type
        let calculatedCdccApproved = originalItem?.cdccApproved ?? 0;
        if ((isProjectOnlyItem || isMergedItem) && dependentFieldsChanged) {
          // For project-only and merged items, always recalculate cdccApproved when dependent fields change
          calculatedCdccApproved = newCompleteThisPeriod + newStoredThisPeriod;
        } else if (!isProjectOnlyItem && !isMergedItem && dependentFieldsChanged) {
          // For payapp-only items, check if it was manually overridden
          const oldExpectedCdccApproved = oldCompleteThisPeriod + oldStoredThisPeriod;
          const currentCdccApproved = originalItem?.cdccApproved ?? 0;
          const isManuallyOverridden = Math.abs(currentCdccApproved - oldExpectedCdccApproved) > 0.01;

          if (!isManuallyOverridden) {
            calculatedCdccApproved = newCompleteThisPeriod + newStoredThisPeriod;
          }
        }

        const nextDivision = lineItem.divisionId
          ? divisionsData.find(division => division.id === lineItem.divisionId) || undefined
          : undefined;

        const previousApplication = lineItem.previousApplication ?? originalItem?.previousApplication ?? 0;
        const newCompletedStoredToDate = previousApplication + calculatedCdccApproved;
        let retainage = lineItem.retainage ?? originalItem?.retainage;
        let manualOverrides = lineItem.manualOverrides ?? originalItem?.manualOverrides;
        const retainageChangedDirectly = lineItem.retainage !== undefined && (originalItem?.retainage ?? 0) !== lineItem.retainage;
        const completedStoredBeforeEdit = originalItem ? calculateTotalCompletedStored(originalItem) : newCompletedStoredToDate;
        const retainageInputsChanged = newCompletedStoredToDate !== completedStoredBeforeEdit;

        if (retainageChangedDirectly) {
          const expectedRetainage = Math.round(newCompletedStoredToDate * ((drawInputRetainagePercentage || 0) / 100));
          manualOverrides = { ...manualOverrides, retainage: (retainage ?? 0) !== expectedRetainage };
        } else if (!originalItem?.manualOverrides?.retainage && retainageInputsChanged && drawInputRetainagePercentage) {
          const expectedRetainageBeforeEdit = Math.round(completedStoredBeforeEdit * (drawInputRetainagePercentage / 100));
          if (Math.round(originalItem?.retainage ?? 0) !== expectedRetainageBeforeEdit) {
            manualOverrides = { ...manualOverrides, retainage: true };
          } else {
            retainage = Math.round(newCompletedStoredToDate * (drawInputRetainagePercentage / 100));
          }
        }

        const basePayappRow = originalItem?.payappRow;
        const payappRow = isManualUpdate && basePayappRow ? { ...basePayappRow, retainage } : basePayappRow;

        // Create updated line item with all required fields.
        // Keep nested lineItem fields in sync because downstream save paths
        // read description/unit/division from updatedLineItem.lineItem.
        const updatedLineItem: ProjectLineItemDTO = {
          ...(originalItem as ProjectLineItemDTO),
          ...lineItem,
          lineItem: {
            ...(originalItem?.lineItem || {}),
            id: originalItem?.lineItem?.id ?? '',
            description: lineItem.description ?? originalItem?.lineItem?.description ?? '',
            unit: lineItem.unit ?? originalItem?.lineItem?.unit ?? LineItemUnitEnum.SF,
            division: nextDivision ?? originalItem?.lineItem?.division,
          },
          cdccApproved: calculatedCdccApproved,
          retainage,
          manualOverrides,
          payappRow,
          isDeleted: false,
          isCreated: originalItem?.isCreated ? true : false,
          isEdited: !originalItem?.isCreated,
        };

        setProjectDraw({
          ...projectDraw,
          lineItems: projectDraw.lineItems?.map(item => (item.id === selectedLineItem.id ? updatedLineItem : item)),
        });
      } else {
        const newLineItem: ProjectLineItemDTO = {
          id: `temp-${Date.now()}`,
          originalBudget: lineItem.originalBudget || 0,
          changeOrder: lineItem.changeOrder || 0,
          reallocation: lineItem.reallocation || 0,
          previousApplication: lineItem.previousApplication || 0,
          completeThisPeriod: lineItem.completeThisPeriod || 0,
          storedThisPeriod: lineItem.storedThisPeriod || 0,
          quantity: lineItem.quantity || 0,
          serialNumber: 0,
          revisedBudget: 0,
          cdccApproved: (lineItem.completeThisPeriod || 0) + (lineItem.storedThisPeriod || 0),
          completedStoredToDate: 0,
          percentageComplete: 0,
          balanceToComplete: 0,
          retainage: lineItem.retainage || 0,
          lineItem: {
            id: '',
            description: lineItem.description || '',
            unit: lineItem.unit || 'unit',
            division: divisionsData.find(division => division.id === lineItem.divisionId) || undefined,
          },
          projectDrawId: projectDraw.projectDrawSummary.id,
          projectDrawName: projectDraw.projectDrawSummary.name,
          isIncluded: false,
          isCreated: true,
          isEdited: false,
          isDeleted: false,
        };

        setProjectDraw({
          ...projectDraw,
          lineItems: projectDraw.lineItems ? [...projectDraw.lineItems, newLineItem] : [newLineItem],
        });
      }
    } catch (error) {
      console.error('Error creating/editing line item:', error);
      toast.error('An error occurred while creating/editing the line item.');
    } finally {
      setAddingLineItem(false);
      setEditDrawLineItemDialogOpen(false);
    }
  };

  // Recalculate retainage for all non-manually-overridden line items when the retainage % itself changes.
  const handleRetainagePercentageChange = (newPercentageDisplay: number) => {
    const oldDecimal = (drawInputRetainagePercentage || 0) / 100;
    const newDecimal = newPercentageDisplay / 100;

    setProjectDraw({
      ...projectDraw,
      projectDrawSummary: {
        ...projectDraw.projectDrawSummary,
        inputRetainagePercentage: newPercentageDisplay,
      },
      lineItems: projectDraw.lineItems?.map(item => {
        const totalCompletedStored = calculateTotalCompletedStored(item);
        // An override that matches the new % is no longer an override.
        if (item.manualOverrides?.retainage && (item.retainage || 0) === Math.round(totalCompletedStored * newDecimal)) {
          return { ...item, manualOverrides: { ...item.manualOverrides, retainage: false }, isEdited: !item.isCreated };
        }
        if (item.manualOverrides?.retainage) return item;
        const oldExpected = Math.round(totalCompletedStored * oldDecimal);
        // Skip items whose value doesn't match the old expected — they're user-overridden but not flagged as such.
        if (Math.round(item.retainage || 0) !== oldExpected) return item;
        const retainage = Math.round(totalCompletedStored * newDecimal);
        const payappRow = isManualUpdate && item.payappRow ? { ...item.payappRow, retainage } : item.payappRow;
        return {
          ...item,
          retainage,
          payappRow,
          isEdited: !item.isCreated,
        };
      }),
    });
  };

  const handleDeleteLineItemClick = async (row: ProjectLineItemDTO) => {
    if (!row.id) {
      toast.error('No Line Item to delete. Please try again.');
      return;
    }

    try {
      const isConfirmed = await getConfirmation({
        heading: `Delete ${row.lineItem.description}`,
        description: 'Are you sure you want to delete this line item?',
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      const { updatedSubtotals, reanchors } = computeSubtotalReanchors(
        new Set([row.id]),
        projectDraw.subtotals ?? [],
        projectDraw.lineItems ?? [],
      );

      setProjectDraw({
        ...projectDraw,
        lineItems: projectDraw.lineItems?.map(item => {
          if (item.id === row.id) {
            return {
              ...item,
              isDeleted: true,
              isEdited: false,
              isCreated: false,
            };
          }
          return item;
        }),
        subtotals: updatedSubtotals,
        pendingSubtotalReanchors:
          reanchors.length > 0 ? [...(projectDraw.pendingSubtotalReanchors ?? []), ...reanchors] : projectDraw.pendingSubtotalReanchors,
      });
      if (row.id && rowSelection[row.id]) {
        const { [row.id]: _, ...remaining } = rowSelection;
        setRowSelection(remaining);
      }
    } catch (error) {
      console.error('Error deleting line item:', error);
      toast.error('An error occurred while deleting the line item.');
    }
  };

  const handleBulkDeleteLineItemClick = async () => {
    // Get selected rows from the table's row selection state. Subtotal rows can't be selected
    // (see enableRowSelection below), but filter defensively in case selection state is stale.
    const lineItemIds = new Set((projectDraw.lineItems ?? []).map(li => li.id));
    const selectedRows = Object.keys(rowSelection).filter(id => lineItemIds.has(id));

    if (selectedRows.length === 0) {
      toast.error('No Line Items to delete. Please select one or more rows.');
      return;
    }

    setBulkDeleteInProgress(true);

    try {
      const isConfirmed = await getConfirmation({
        heading: `Delete ${selectedRows.length} Line Item(s)`,
        description: 'Are you sure you want to delete these line items?',
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      const { updatedSubtotals, reanchors } = computeSubtotalReanchors(
        new Set(selectedRows),
        projectDraw.subtotals ?? [],
        projectDraw.lineItems ?? [],
      );

      setProjectDraw({
        ...projectDraw,
        lineItems: projectDraw.lineItems?.map(item => {
          if (selectedRows.includes(item.id)) {
            return {
              ...item,
              isDeleted: true,
              isEdited: false,
              isCreated: false,
            };
          }
          return item;
        }),
        subtotals: updatedSubtotals,
        pendingSubtotalReanchors:
          reanchors.length > 0 ? [...(projectDraw.pendingSubtotalReanchors ?? []), ...reanchors] : projectDraw.pendingSubtotalReanchors,
      });

      // Clear selection after successful delete
      setRowSelection({});
    } catch (error) {
      console.error('Error bulk deleting line items:', error);
      toast.error('An error occurred while deleting the line items.');
    } finally {
      setBulkDeleteInProgress(false);
    }
  };

  const columns = React.useMemo<MRT_ColumnDef<ProjectLineItemDTO>[]>(() => {
    const calculatedColumnBackground = REPORT_COLORS.YELLOW;
    const getCalculatedBodyCellProps = (cell: MRT_Cell<ProjectLineItemDTO>) => {
      const baseProps = excel.getBodyCellProps({ cell });
      const isSubtotalRow = cell.row.original?.__rowType === 'subtotal';
      return {
        ...baseProps,
        align: 'right' as const,
        sx: {
          ...(baseProps as { sx?: Record<string, unknown> }).sx,
          backgroundColor: isSubtotalRow ? SUBTOTAL_ROW_BACKGROUND_COLOR + ' !important' : calculatedColumnBackground,
          justifyContent: 'flex-end',
          border: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        },
      };
    };

    const rightAlignedProps = {
      muiTableBodyCellProps: {
        align: 'right' as const,
        sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
      },
      muiTableHeadCellProps: {
        align: 'right' as const,
        sx: (theme: any) => ({
          backgroundColor: theme.palette.primary.main,
          color: '#ffffff',
          top: 0,
          height: '50px',
          padding: '6px 6px',
          fontSize: '0.875rem',
          fontWeight: 'normal',
          border: 'none',
          position: 'relative',
          justifyContent: 'center',
          whiteSpace: 'normal',
          '&[data-pinned="true"]:not([role="columnheader"])::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: theme.palette.primary.main,
            zIndex: 0,
          },
          '&[data-pinned="true"]:not([role="columnheader"])': {
            zIndex: 1,
          },
          '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '20%',
            height: '60%',
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
          },
          '& .Mui-TableHeadCell-Content-Wrapper': {
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            whiteSpace: 'normal',
          },
        }),
      },
      muiTableFooterCellProps: {
        sx: (theme: { palette: { primary: { main: any } } }) => ({
          backgroundColor: theme.palette.primary.main,
          color: '#ffffff',
          fontWeight: 'bold',
          padding: '8px 9px',
          border: 'none',

          // Sticky footer cell background via ::before
          '&[data-pinned="true"]::before': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: theme.palette.primary.main,
            zIndex: 0,
          },

          // Maintain stickiness & layer above ::before
          '&[data-pinned="true"]': {
            zIndex: 1,
          },

          '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '20%',
            height: '60%',
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
          },
        }),
      },
    };

    const getColumnWidth = (headerLabel: string, values: number[]) => {
      const longestValue = values.reduce((longest, val) => {
        const f = formatCurrency(Math.round(val).toString(), false);
        return f.length > longest.length ? f : longest;
      }, '');
      const longestHeaderWord = headerLabel.split(' ').reduce((longest, word) => (word.length > longest.length ? word : longest), '');
      const headerWidth = Math.ceil(longestHeaderWord.length * 11 + 54);
      const valueWidth = Math.ceil(longestValue.length * 7.5 + 24);
      return Math.max(headerWidth, valueWidth, 120);
    };

    const revisedBudgetValues = filteredLineItems.map(
      item => (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0),
    );
    const completedStoredToDateValues = filteredLineItems.map(item => (item.previousApplication || 0) + (item.cdccApproved || 0));
    const balanceToCompleteValues = filteredLineItems.map((item, i) => revisedBudgetValues[i] - completedStoredToDateValues[i]);

    const numericColumnWidths: Record<string, number> = {
      originalBudget: getColumnWidth(
        'Original Budget',
        filteredLineItems.map(item => item.originalBudget || 0),
      ),
      reallocation: getColumnWidth(
        'Reallocation',
        filteredLineItems.map(item => item.reallocation || 0),
      ),
      changeOrder: getColumnWidth(
        'Change Order',
        filteredLineItems.map(item => item.changeOrder || 0),
      ),
      revisedBudget: getColumnWidth('Revised Budget', revisedBudgetValues),
      previousApplication: getColumnWidth(
        'Previous Application',
        filteredLineItems.map(item => item.previousApplication || 0),
      ),
      completeThisPeriod: getColumnWidth(
        'Complete This Period',
        filteredLineItems.map(item => item.completeThisPeriod || 0),
      ),
      storedThisPeriod: getColumnWidth(
        'Stored This Period',
        filteredLineItems.map(item => item.storedThisPeriod || 0),
      ),
      completedStoredToDate: getColumnWidth('Completed Stored To Date', completedStoredToDateValues),
      percentageComplete: 140,
      balanceToComplete: getColumnWidth('Balance To Complete', balanceToCompleteValues),
      retainage: getColumnWidth(
        'Retainage',
        filteredLineItems.map(item => item.retainage || 0),
      ),
    };

    const baseColumns: Omit<MRT_ColumnDef<ProjectLineItemDTO>, 'size'>[] = [
      {
        accessorKey: 'lineItem.description',
        header: 'Description',
        Cell: ({ cell, row }: { cell: MRT_Cell<ProjectLineItemDTO>; row: MRT_Row<ProjectLineItemDTO> }) => {
          if (row.original.__rowType === 'subtotal') {
            const label = cell.getValue<string>();
            // Subtotal rows aren't draggable (no DraggableDescriptionCell wrapper) and can't be
            // dropped onto — they simply aren't a registered drag/drop target.
            return (
              <Stack direction='row' alignItems='center' spacing={1} sx={{ width: '100%' }}>
                <Functions fontSize='small' sx={{ color: 'primary.main' }} />
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  {label}
                </Typography>
              </Stack>
            );
          }

          const description = cell.getValue<string>();
          const isChangeOrder = (row.original.labels || []).includes(LineItemLabelEnum.ChangeOrder);
          const descriptionText = description == null ? '' : String(description);
          const tooltipText = isChangeOrder ? `CO: ${descriptionText}` : descriptionText;
          const prefix = isChangeOrder ? <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>CO:</span> : undefined;

          const content = row.original.payappRow ? (
            // Determine if payapp-only row has discrepancy
            (() => {
              const payappOnly = !row.original?.projectRow;
              const monitored = ['reallocation', 'completedStoredToDate', 'balanceToComplete']; // calculated columns that can have discrepancy for payapp-only rows
              const unmonitored = [
                'originalBudget',
                'changeOrder',
                'revisedBudget',
                'previousApplication',
                'completeThisPeriod',
                'storedThisPeriod',
                'percentageComplete',
                'balanceToComplete',
                'retainage',
              ];

              const hasUnmonitoredDiff = unmonitored.some(f => {
                const sysVal = row.getValue(f);
                const payVal = row.original?.payappRow?.[f as keyof typeof row.original.payappRow];
                return sysVal !== undefined && payVal !== undefined && differsWithBuffer(sysVal, payVal);
              });

              const rowHasMonitoredDiff = monitored.some(field => {
                const sysRaw = row.getValue(field);
                const sys = typeof sysRaw === 'number' ? sysRaw : Number(sysRaw) || 0;
                const pay = row.original?.payappRow?.[field as keyof typeof row.original.payappRow];
                return (
                  sys !== undefined && pay !== undefined && differsWithBuffer(Number(sys.toFixed(2)), Number(Number(pay ?? 0).toFixed(2)))
                );
              });

              const displaySubheadings =
                (payappOnly && rowHasMonitoredDiff) || (!payappOnly && (rowHasMonitoredDiff || hasUnmonitoredDiff));

              return (
                <Stack sx={{ width: '100%' }}>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TruncatedCell text={descriptionText} tooltipText={tooltipText} prefix={prefix} />
                    </Box>
                  </Stack>
                  {displaySubheadings && (
                    <>
                      <Typography variant='caption'>Payapp Value</Typography>
                      {row.original?.projectRow && <Typography variant='caption'>Last Month Value</Typography>}
                    </>
                  )}
                </Stack>
              );
            })()
          ) : (
            <Stack direction='row' alignItems='center' spacing={1} sx={{ width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TruncatedCell text={descriptionText} tooltipText={tooltipText} prefix={prefix} />
              </Box>
            </Stack>
          );

          // Drag by Description cell to reorder (no separate drag icon)
          return (
            <DraggableDescriptionCell rowId={row.id} disabled={reorderDisabled} moveRow={moveRow}>
              {content}
            </DraggableDescriptionCell>
          );
        },
        muiTableBodyCellProps: ({ cell }) => excel.getBodyCellProps({ cell }),
        muiEditTextFieldProps: ({ cell }) => ({
          ...excel.getEditTextFieldProps({ cell }),
        }),
        Footer: () => 'Totals',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
      },
      {
        id: 'division',
        header: 'Division',
        accessorKey: 'lineItem.division.code',
        Cell: ({ row }: { row: MRT_Row<ProjectLineItemDTO> }) => {
          const division = row.original.lineItem.division;
          const isTotalRow = row.id === 'totals';
          const editsNotAllowed = readonly || isTotalRow;
          return (
            <Box
              component='span'
              sx={{
                color: editsNotAllowed ? 'inherit' : 'blue',
                fontStyle: division ? 'normal' : 'italic',
                cursor: editsNotAllowed ? 'default' : 'pointer',
              }}
            >
              {division ? `${division.code} - ${division.name}` : editsNotAllowed ? '-' : 'Click to edit'}
            </Box>
          );
        },
        Edit: ({ cell, table }: { cell: MRT_Cell<ProjectLineItemDTO>; table: MRT_TableInstance<ProjectLineItemDTO> }) => {
          const handleChange = (divisionId: string | null) => {
            const division = divisions.find(d => d.id === divisionId);

            // Update the line item data
            const updatedLineItem: ProjectLineItemDTO = {
              ...cell.row.original,
              lineItem: {
                ...cell.row.original.lineItem,
                division: division,
              },
            };

            // Call the existing update handler
            handleEditDrawLineItem(updatedLineItem);

            // Exit editing mode
            table.setEditingCell(null);
          };

          return (
            <SelectDivisionFromGiven
              divisions={divisions}
              selectedDivision={cell.row.original.lineItem.division?.id ?? null}
              setSelectedDivision={handleChange}
            />
          );
        },
        muiTableBodyCellProps: ({ cell }) => excel.getBodyCellProps({ cell }),
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Footer: () => '',
      },
      {
        accessorKey: 'originalBudget',
        header: 'Original Budget',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Cell: renderColCell(true, undefined, isSecondPayappFlow),
        muiTableBodyCellProps: ({ cell }) => ({
          ...excel.getBodyCellProps({ cell }),
          align: 'right' as const,
          sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
        }),
        muiEditTextFieldProps: ({ cell }) => ({
          ...excel.getEditTextFieldProps({ cell }),
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        Footer: () => formatCurrency(totalsLineItem.originalBudget.toString(), false),
      },
      {
        accessorKey: 'reallocation',
        header: 'Reallocation',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Cell: renderColCell(true, undefined, isSecondPayappFlow),
        muiTableBodyCellProps: ({ cell }) => ({
          ...excel.getBodyCellProps({ cell }),
          align: 'right' as const,
          sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
        }),
        muiEditTextFieldProps: ({ cell }) => ({
          ...excel.getEditTextFieldProps({ cell }),
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        Footer: () => formatCurrency(totalsLineItem.reallocation.toString(), true, false, true),
      },
      {
        accessorKey: 'changeOrder',
        header: 'Change Order',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Cell: renderColCell(true, undefined, isSecondPayappFlow),
        muiTableBodyCellProps: ({ cell }) => ({
          ...excel.getBodyCellProps({ cell }),
          align: 'right' as const,
          sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
        }),
        muiEditTextFieldProps: ({ cell }) => ({
          ...excel.getEditTextFieldProps({ cell }),
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        Footer: () => formatCurrency(totalsLineItem.changeOrder.toString(), false),
      },
      {
        header: 'Revised Budget',
        id: 'revisedBudget',
        Cell: renderColCell(true, undefined, isSecondPayappFlow),
        accessorFn: (row: ProjectLineItemDTO) => {
          return (row.originalBudget || 0) + (row.reallocation || 0) + (row.changeOrder || 0);
        },
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        enableEditing: false,
        Footer: () => {
          const revisedBudget =
            (totalsLineItem.originalBudget || 0) + (totalsLineItem.reallocation || 0) + (totalsLineItem.changeOrder || 0);
          return formatCurrency(revisedBudget.toString(), false);
        },
      },
      {
        accessorKey: 'previousApplication',
        header: 'Previous Application',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Cell: renderColCell(true, undefined, isSecondPayappFlow, handleSkippedPayapp, isManualUpdate),
        muiTableBodyCellProps: ({ cell }) => ({
          ...excel.getBodyCellProps({ cell }),
          align: 'right' as const,
          sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
        }),
        muiEditTextFieldProps: ({ cell }) => ({
          ...excel.getEditTextFieldProps({ cell }),
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        Footer: () => formatCurrency(totalsLineItem.previousApplication.toString(), false),
      },
      {
        accessorKey: 'completeThisPeriod',
        header: 'Complete This Period',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Cell: renderColCell(true, undefined, isSecondPayappFlow, handleSkippedPayapp),
        muiTableBodyCellProps: ({ cell }) => ({
          ...excel.getBodyCellProps({ cell }),
          align: 'right' as const,
          sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
        }),
        muiEditTextFieldProps: ({ cell }) => ({
          ...excel.getEditTextFieldProps({ cell }),
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        Footer: () => formatCurrency(totalsLineItem.completeThisPeriod.toString(), false),
      },
      {
        accessorKey: 'storedThisPeriod',
        header: 'Stored This Period',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Cell: renderColCell(true, undefined, isSecondPayappFlow, handleSkippedPayapp),
        muiTableBodyCellProps: ({ cell }) => ({
          ...excel.getBodyCellProps({ cell }),
          align: 'right' as const,
          sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
        }),
        muiEditTextFieldProps: ({ cell }) => ({
          ...excel.getEditTextFieldProps({ cell }),
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        Footer: () => formatCurrency(totalsLineItem.storedThisPeriod.toString(), false),
      },
      {
        header: 'Completed Stored To Date',
        id: 'completedStoredToDate',
        Cell: renderColCell(true, undefined, isSecondPayappFlow, handleSkippedPayapp),
        accessorFn: (row: ProjectLineItemDTO) => {
          return (row.previousApplication || 0) + (row.cdccApproved || 0);
        },
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        enableEditing: false,
        Footer: () => {
          const completedStoredToDate =
            (totalsLineItem.previousApplication || 0) + ((totalsLineItem.completeThisPeriod || 0) + (totalsLineItem.storedThisPeriod || 0));
          return formatCurrency(completedStoredToDate.toString(), false);
        },
      },
      {
        header: 'Percentage Complete',
        id: 'percentageComplete',
        Cell: renderColCell(true, undefined, isSecondPayappFlow),
        accessorFn: (row: ProjectLineItemDTO) => {
          const revisedBudget = (row.originalBudget || 0) + (row.reallocation || 0) + (row.changeOrder || 0);
          const completedStoredToDate = (row.previousApplication || 0) + (row.cdccApproved || 0);
          return revisedBudget !== 0 ? (completedStoredToDate / revisedBudget) * 100 : 0;
        },
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        enableEditing: false,
        Footer: () => '',
      },
      {
        header: 'Balance To Complete',
        id: 'balanceToComplete',
        Cell: renderColCell(true, undefined, isSecondPayappFlow),
        accessorFn: (row: ProjectLineItemDTO) => {
          const revisedBudget = (row.originalBudget || 0) + (row.reallocation || 0) + (row.changeOrder || 0);
          const completedStoredToDate = (row.previousApplication || 0) + (row.cdccApproved || 0);
          return revisedBudget - completedStoredToDate;
        },
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        enableEditing: false,
        Footer: () => {
          const revisedBudget =
            (totalsLineItem.originalBudget || 0) + (totalsLineItem.reallocation || 0) + (totalsLineItem.changeOrder || 0);
          const completedStoredToDate =
            (totalsLineItem.previousApplication || 0) + ((totalsLineItem.completeThisPeriod || 0) + (totalsLineItem.storedThisPeriod || 0));
          const balance = revisedBudget - completedStoredToDate;
          return formatCurrency(balance.toString(), false);
        },
      },
      {
        accessorKey: 'retainage',
        header: 'Retainage',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        Cell: renderColCell(true, drawInputRetainagePercentage, isSecondPayappFlow),
        muiTableBodyCellProps: ({ cell }) => ({
          ...excel.getBodyCellProps({ cell }),
          align: 'right' as const,
          sx: { justifyContent: 'flex-end', border: 'none', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
        }),
        muiEditTextFieldProps: ({ cell }: { cell: MRT_Cell<ProjectLineItemDTO> }) => ({
          ...excel.getEditTextFieldProps({ cell }),
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        Footer: () => formatCurrency(totalsLineItem.retainage.toString(), false),
      },
    ];

    // Add size properties to columns
    const columnsWithSizes = baseColumns.map(column => {
      if (column.accessorKey === 'lineItem.description') {
        return { ...column, size: 300 };
      }
      if (column.id === 'division') {
        return { ...column, size: 250 };
      }
      if (column.id === 'actions') {
        return { ...column, size: 160 };
      }
      const columnId = column.id ?? column.accessorKey ?? '';
      return { ...column, size: numericColumnWidths[columnId] ?? 200 };
    });

    return columnsWithSizes;
  }, [
    readonly,
    divisions,
    drawInputRetainagePercentage,
    totalsLineItem,
    excel,
    handleSkippedPayapp,
    isSecondPayappFlow,
    isManualUpdate,
    filteredLineItems,
  ]);

  const [aiMatchInProgress, setAiMatchInProgress] = React.useState(false);
  const aiMatchRequest = React.useMemo(() => getAiMatchRequest(projectDraw), [projectDraw]);
  const canSuggestWithAi = aiMatchRequest.payappRows.length > 0 && aiMatchRequest.projectRows.length > 0;
  // The AI call takes seconds and the table stays editable, so apply results to the latest draw, not the one at click time
  const latestProjectDrawRef = React.useRef(projectDraw);
  latestProjectDrawRef.current = projectDraw;

  const handleSuggestWithAi = async () => {
    setAiMatchInProgress(true);
    try {
      const matches = await matchPayappLineItems(aiMatchRequest);
      const latestDraw = latestProjectDrawRef.current;
      const newDraw = applyAiMatches(latestDraw, matches ?? []);
      if (newDraw === latestDraw) {
        toast.info('AI found no confident matches for the unmerged rows.');
        return;
      }
      setProjectDraw(newDraw);
    } catch (error) {
      console.error('Error suggesting matches with AI:', error);
      toast.error('Failed to get AI suggestions. Please try again.');
    } finally {
      setAiMatchInProgress(false);
    }
  };

  const handleMergeAll = () => {
    let lineItems = [...(projectDraw.lineItems ?? [])];
    const processedGroupIds = new Set<string>();

    for (const payappItem of lineItems) {
      if (!payappItem.groupId || payappItem.matchType !== 'fuzzy' || !payappItem.isCreated || processedGroupIds.has(payappItem.groupId)) {
        continue;
      }
      const groupId = payappItem.groupId;
      const payappId = payappItem.id;
      const projectItem = lineItems.find(item => item.groupId === groupId && item.exitingAndNotMatched && item.matchType === 'fuzzy');
      if (!projectItem) continue;

      const freshPayapp = lineItems.find(i => i.id === payappId);
      const freshProject = lineItems.find(i => i.id === projectItem.id);
      if (freshPayapp && freshProject) {
        lineItems = autoMergeFuzzyMatch(freshPayapp, freshProject, lineItems, assignedPayappColumns);
        processedGroupIds.add(groupId);
      }
    }

    setProjectDraw({ ...projectDraw, lineItems });
  };

  const handleMergeSelectedItems = async () => {
    try {
      const selectedRows = Object.keys(rowSelection);
      const selectedItems = selectedRows.map(id => filteredLineItems.find(item => item.id === id)).filter(Boolean) as ProjectLineItemDTO[];

      if (selectedItems.length !== 2) {
        toast.error('Please select exactly two items to merge');
        return;
      }

      // Find payapp and project items
      const payappItem = selectedItems.find(item => item.isCreated && !item.exitingAndNotMatched);
      const projectItem = selectedItems.find(item => !item.isCreated && item.exitingAndNotMatched);

      if (!payappItem || !projectItem) {
        toast.error('Please select one payapp item and one project item to merge');
        return;
      }

      // Use the existing merge function (sync when no getConfirmation; no await to avoid lag)
      await handleMergeFuzzyMatch(
        payappItem,
        filteredLineItems,
        projectDraw,
        setProjectDraw,
        undefined,
        projectItem.id,
        assignedPayappColumns,
      );
      onMerge?.(payappItem, projectItem, 'selected');

      // Clear selection after merge
      setRowSelection({});
    } catch (error) {
      console.error('Error merging items:', error);
      toast.error('Failed to merge items. Please try again.');
    }
  };

  const handleCreateLineItemClick = () => {
    setEditDrawLineItemDialogOpen(true);
    setSelectedLineItem(null);
  };

  const handleUpdateLineItemClick = (row: any) => {
    setSelectedLineItem(row);
    setEditDrawLineItemDialogOpen(true);
  };

  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  const renderHeader = (inFullScreen: boolean) => (
    <Box
      px={inFullScreen ? 0 : 2}
      mb={inFullScreen ? 0 : 1}
      width='100%'
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      sx={{ boxSizing: 'border-box' }}
    >
      <Box display='flex' alignItems='center'>
        <List color='primary' sx={{ fontSize: '24px !important', fontWeight: '700 !important' }} />
        <Typography variant='h5' color='primary' sx={{ ml: 1, mb: 0 }}>
          Line Items
        </Typography>
        <Tooltip
          title={payappProcessTooltips.CompareAgainstLastMonthLineItems}
          placement='bottom'
          arrow={false}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 'none',
                padding: '12px 16px',
              },
            },
          }}
        >
          <Help sx={{ ml: 1, cursor: 'pointer', fontSize: '20px' }} />
        </Tooltip>
        <Typography component='span' variant='body2' sx={{ ml: 1, color: 'text.secondary' }}>
          (Total Line Items: {filteredData.length}
          {filteredData.length !== filteredLineItems.length && ` (${filteredLineItems.length} total)`})
        </Typography>
      </Box>
      {!readonly && (
        <Stack direction={'row'} spacing={1} alignItems='center'>
          {isManualUpdate && (
            <Tooltip title='Retainage percentage applied to line items in this draw' arrow>
              <Box
                display='flex'
                alignItems='center'
                sx={{
                  mr: 2,
                  pl: 1.5,
                  pr: 1,
                  height: 36,
                  borderRadius: 20,
                  bgcolor: 'rgba(0, 0, 0, 0.06)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' },
                  '&:has(input:focus)': { bgcolor: 'rgba(0, 0, 0, 0.1)' },
                }}
              >
                <Typography color='text.secondary' sx={{ mr: 1, whiteSpace: 'nowrap', fontWeight: 500, fontSize: '14px' }}>
                  Retainage
                </Typography>
                <TextField
                  placeholder='10'
                  variant='standard'
                  value={inputRetainagePercentageDraft}
                  onChange={e => setInputRetainagePercentageDraft(e.target.value)}
                  onBlur={() => {
                    const parsed = parseFloat(inputRetainagePercentageDraft as string);
                    // Blank/invalid input reverts to the current % rather than silently applying 0%.
                    if (isNaN(parsed)) {
                      setInputRetainagePercentageDraft(drawInputRetainagePercentage ?? 0);
                      return;
                    }
                    if (parsed === (drawInputRetainagePercentage ?? 0)) return;
                    handleRetainagePercentageChange(parsed);
                  }}
                  InputProps={{
                    disableUnderline: true,
                    inputComponent: NumericFormatInput as any,
                    endAdornment: (
                      <Typography variant='body2' color='text.secondary' sx={{ ml: 0.25 }}>
                        %
                      </Typography>
                    ),
                  }}
                  sx={{ width: 60 }}
                />
              </Box>
            </Tooltip>
          )}
          <Stack direction='row' spacing={2} alignItems='center' pr={2}>
            {hasFuzzyMatchedRows && (
              <Tooltip
                title='There are line items that can be merged and need your attention. Scroll down to review.'
                placement='bottom'
                arrow
              >
                <WarningAmberIcon color='warning' fontSize='small' />
              </Tooltip>
            )}
            <LegendItem color={REPORT_COLORS.PAYAPP_ONLY_BACKGROUND_COLOR} label='Payapp only' tooltip='Line item coming from payapp' />
            <LegendItem
              color={REPORT_COLORS.PROJECT_ONLY_BACKGROUND_COLOR}
              label='Last Month only'
              tooltip="Line item coming from last month's project"
            />
            <LegendItem color={REPORT_COLORS.YELLOW} label='Calculated' tooltip='Calculated column' />
            <LegendItem
              color={REPORT_COLORS.PAYAPP_PROJECT_MISMATCH_BACKGROUND_COLOR}
              label='Mismatch'
              tooltip='Review this cell — value differs between payapp and project'
              border={`1.5px solid ${REPORT_COLORS.PAYAPP_PROJECT_MISMATCH_BORDER_COLOR}`}
            />
          </Stack>
          {(hasFuzzyMatchedRows || hasMergedRows) && (
            <Tooltip title='Hide merged rows — show only unmerged line items' arrow={false}>
              <FormControlLabel
                control={<Switch size='small' checked={showUnmergedOnly} onChange={() => setShowUnmergedOnly(prev => !prev)} />}
                label={
                  <Typography variant='caption' sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    View Unmerged
                  </Typography>
                }
                sx={{ mx: 0, gap: 0.5 }}
              />
            </Tooltip>
          )}
          <IconOverTextButton
            icon={<Search />}
            text='Search'
            tooltip='Search line items'
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            sunken={isSearchOpen}
            showHoverEffect={true}
          />
          <Box
            sx={{
              ml: isSearchOpen ? 1 : 0,
              mr: 0,
              width: isSearchOpen ? '200px' : 0,
              transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out',
              overflow: 'hidden',
              opacity: isSearchOpen ? 1 : 0,
            }}
          >
            <TextField
              placeholder='Search'
              size='medium'
              value={table.getState().globalFilter ?? ''}
              onChange={event => table.setGlobalFilter(event.target.value)}
              fullWidth
              InputProps={{ startAdornment: null }}
            />
          </Box>

          <Stack direction='row' spacing={0} alignItems='center'>
            {/* Action buttons */}
            {selectedLineItemsIds.length > 0 && (
              <IconOverTextButton
                icon={<Delete />}
                text={`Delete (${selectedLineItemsIds.length})`}
                tooltip='Delete selected line items'
                onClick={handleBulkDeleteLineItemClick}
                loading={bulkDeleteInProgress}
                showHoverEffect={true}
                color='error'
              />
            )}
            {hasFuzzyMatchedRows && (
              <IconOverTextButton
                icon={<CompareArrowsIcon />}
                text='Merge All Suggested'
                tooltip='Automatically merge all suggested fuzzy-matched pairs'
                onClick={handleMergeAll}
                showHoverEffect={true}
              />
            )}
            {!readonly && canSuggestWithAi && (
              <IconOverTextButton
                icon={<AutoAwesome />}
                text='Suggest with AI'
                tooltip='Use AI to suggest pairs for the remaining unmerged rows'
                onClick={handleSuggestWithAi}
                loading={aiMatchInProgress}
                showHoverEffect={true}
              />
            )}
            {canMergeSelectedItems && (
              <IconOverTextButton
                icon={<CompareArrowsIcon />}
                text='Merge Selected'
                tooltip='Merge selected payapp and last month line items'
                onClick={handleMergeSelectedItems}
                showHoverEffect={true}
              />
            )}
            <IconOverTextButton
              icon={<Add />}
              text='Add Line Item'
              tooltip='Add a new line item'
              onClick={handleCreateLineItemClick}
              loading={addingLineItem}
              showHoverEffect={true}
            />

            <ButtonDivider />

            {/* Utility buttons */}
            <IconOverTextButton
              icon={<ViewColumnIcon />}
              text='Columns'
              tooltip='Manage Columns'
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => setColumnsMenuAnchorEl(e.currentTarget)}
              showHoverEffect={false}
              wrapInSpan={true}
            />

            <Badge badgeContent={getAppliedFiltersCount(filterModel, skippedFields)} color='primary' overlap='circular'>
              <IconOverTextButton
                icon={<FilterListIcon />}
                text='Filter'
                tooltip='Filter line items'
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => setFilterAnchorEl(e.currentTarget)}
                showHoverEffect={false}
              />
            </Badge>

            <IconOverTextButton
              icon={isFullScreen ? <FullscreenExit /> : <Fullscreen />}
              text={isFullScreen ? 'Exit Full' : 'Full Screen'}
              tooltip={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
              onClick={() => setIsFullScreen(!isFullScreen)}
              showHoverEffect={false}
            />
          </Stack>
        </Stack>
      )}
    </Box>
  );

  const table = useMaterialReactTable({
    columns,
    data: displayData,
    initialState: {
      showGlobalFilter: false,
      density: 'compact',
      columnPinning: { left: ['mrt-row-select', 'lineItem.description', 'division'], right: ['mrt-row-actions'] },
    },
    enableColumnFilters: false,
    // Sorting would scramble the range-edit preview order, so it's off while a subtotal range is being edited.
    enableSorting: !rangeEditor.rangeEditSubtotalId,
    layoutMode: 'grid',
    enableColumnResizing: true,
    enableColumnPinning: true,
    columnResizeMode: 'onChange',
    enableRowSelection: (row: MRT_Row<ProjectLineItemDTO>) =>
      rangeEditor.rangeEditSubtotalId ? rangeEditor.poolIds.includes(row.original.id) : !readonly && row.original.__rowType !== 'subtotal',
    enableSelectAll: !rangeEditor.rangeEditSubtotalId,
    enableMultiRowSelection: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableColumnActions: false,
    editDisplayMode: 'cell',
    enableEditing: !readonly,
    enableGlobalFilter: true,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnVisibilityChange: setColumnVisibilityModel,
    onSortingChange: getOnSortingChangeWithToast(sorting as any, setSorting as any),
    getRowId: row => row.id,
    positionActionsColumn: 'last',
    enableRowActions: !readonly,
    enableBottomToolbar: false,
    enableTableFooter: true,
    // Show MRT's top toolbar only in fullscreen mode
    enableTopToolbar: isFullScreen,
    enableFullScreenToggle: false,
    // Control fullscreen state so we can react in our layout
    onIsFullScreenChange: newFullScreenState => {
      setIsFullScreen(newFullScreenState);
    },
    renderBottomToolbar: () => null,
    muiSelectAllCheckboxProps: ({ table }) => buildSelectAllCheckboxProps(table),
    muiSelectCheckboxProps: { sx: { padding: '4px' } },
    muiTableContainerProps: {
      ...excel.getContainerProps(),
      ref: tableContainerRef,
      sx: {
        height: isFullScreen ? 'calc(100vh - 110px)' : 'auto',
        maxHeight: isFullScreen ? 'calc(100vh - 110px)' : 'none',
        overflow: isFullScreen ? 'auto' : 'visible',
        ...excelSelectedCellSx,
        '& .MuiTable-root': {
          margin: 0,
          minWidth: 'fit-content',
        },
        // Keep header and footer in view while scrolling
        '& thead, & tfoot': {
          position: 'sticky',
          backgroundColor: 'white',
          zIndex: 2,
        },
        '& thead': {
          top: 0,
          zIndex: 3,
        },
        '& tfoot': {
          bottom: 0,
          zIndex: 2,
          backgroundColor: theme => theme.palette.primary.main,
        },
        // Override MRT's pinned cell alignment for actions column
        '& .css-1jh1yab-MuiTableCell-root[data-column-id="mrt-row-actions"]': {
          justifyContent: 'center !important',
        },
        '& [data-pinned="true"][data-column-id="mrt-row-actions"]': {
          '& .MuiStack-root': {
            justifyContent: 'center !important',
          },
        },
        '& .css-10cmcef-MuiTableCell-root': {
          justifyContent: 'center !important',
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        backgroundColor: 'white',
        height: 'auto',
        overflow: 'visible',
        '& .MuiTable-root': {
          overflow: 'visible',
        },
        '& .MuiTableBody-root': {
          overflow: 'visible',
        },
        // Show toolbar only when fullscreen
        '&[data-fullscreen="true"] .MuiToolbar-root': {
          display: 'flex !important',
        },
        '&:not([data-fullscreen="true"]) .MuiToolbar-root': {
          display: 'none !important',
        },
      },
    },
    renderRowActions: ({ row }: { row: MRT_Row<ProjectLineItemDTO> }) => {
      if (row.original.__rowType === 'subtotal') {
        const isEditingThisRange = rangeEditor.rangeEditSubtotalId === row.original.id;
        const otherRangeEditActive = !!rangeEditor.rangeEditSubtotalId && !isEditingThisRange;

        if (isEditingThisRange) {
          return (
            <Stack direction='row' spacing={1} alignItems='flex-end' justifyContent='center'>
              <Tooltip title='Confirm Range' arrow={false}>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Check fontSize='small' />}
                  textColor='primary'
                  onClick={() => rangeEditor.confirmRangeEdit()}
                />
              </Tooltip>
              <Tooltip title='Cancel' arrow={false}>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Close fontSize='small' />}
                  textColor='error'
                  onClick={() => rangeEditor.cancelRangeEdit()}
                />
              </Tooltip>
            </Stack>
          );
        }

        return (
          <Stack direction='row' spacing={1} alignItems='flex-end' justifyContent='center'>
            <Tooltip title='Edit Range' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Edit fontSize='small' />}
                  textColor='primary'
                  onClick={() => {
                    setSorting([]);
                    rangeEditor.startRangeEdit(row.original.id);
                  }}
                  disabled={otherRangeEditActive}
                />
              </span>
            </Tooltip>
            <Tooltip title='Delete Subtotal' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Delete fontSize='small' />}
                  textColor='error'
                  onClick={() => handleDeleteSubtotalClick(row.original.id)}
                  disabled={otherRangeEditActive}
                />
              </span>
            </Tooltip>
          </Stack>
        );
      }

      return (
        <Stack direction='row' spacing={1} alignItems='flex-end' justifyContent='center'>
          {/* Merge or Undo button - only one shows at a time, in the same position */}
          {row.original.matchType === 'fuzzy' && row.original.isCreated && row.original.groupId?.startsWith('fuzzy-group') ? (
            <Tooltip title='Merge with last month line item below' arrow={false}>
              <WhiteFab
                size='small'
                textColor='primary'
                icon={<CompareArrowsIcon fontSize='small' />}
                onClick={async () => {
                  const projectItem = filteredLineItems.find(item => item.groupId === row.original.groupId && item.exitingAndNotMatched);
                  await handleMergeFuzzyMatch(
                    row.original,
                    filteredLineItems,
                    projectDraw,
                    setProjectDraw,
                    undefined,
                    undefined,
                    assignedPayappColumns,
                  );
                  onMerge?.(row.original, projectItem);
                }}
                sx={{ ml: 1, width: 32, height: 32, minHeight: 32 }}
              />
            </Tooltip>
          ) : row.original.matchType === 'exact' &&
            row.original.payappRow &&
            row.original.projectRow &&
            (row.original.payappRow as any).id &&
            (row.original.projectRow as any).id ? (
            <Tooltip
              title={
                matchMode === 'budget'
                  ? 'Unmerge and re-match by budget'
                  : matchMode === 'previousApplication'
                    ? 'Unmerge and re-match by previous application'
                    : 'Unmerge — split back into separate payapp and last month rows'
              }
              arrow={false}
            >
              <WhiteFab
                size='small'
                textColor='secondary'
                icon={<Undo fontSize='small' />}
                onClick={() => {
                  if (matchMode !== 'description') {
                    undoMergeFuzzyMatch(row.original, projectDraw, undoneResult => {
                      setProjectDraw(rematch(undoneResult as ProjectLineItemsAndSummary, matchMode));
                    });
                  } else {
                    undoMergeFuzzyMatch(row.original, projectDraw, setProjectDraw);
                  }
                }}
                sx={{ ml: 1, width: 32, height: 32, minHeight: 32 }}
              />
            </Tooltip>
          ) : null}
          {/* Add extra WhiteFab if merge/undo buttons exist on other rows to align with header */}
          {!(
            (row.original.matchType === 'fuzzy' && row.original.isCreated && row.original.groupId?.startsWith('fuzzy-group')) ||
            (row.original.matchType === 'exact' &&
              row.original.payappRow &&
              row.original.projectRow &&
              (row.original.payappRow as any).id &&
              (row.original.projectRow as any).id)
          ) &&
            (hasFuzzyMatchedRows || hasMergedRows) && (
              <WhiteFab
                size='small'
                sx={{ width: 32, height: 32, minHeight: 32, opacity: 0 }}
                icon={<Delete fontSize='small' />}
                textColor='error'
                disabled
              />
            )}

          <Tooltip title='Edit Line Item' arrow={false}>
            <WhiteFab
              size='small'
              sx={{ width: 32, height: 32, minHeight: 32 }}
              icon={<Edit fontSize='small' />}
              textColor='primary'
              onClick={() => handleUpdateLineItemClick(row.original)}
              disabled={selectedLineItemsIds.length > 0 || !!rangeEditor.rangeEditSubtotalId}
            />
          </Tooltip>
          <Tooltip title='Delete Line Item' arrow={false}>
            <WhiteFab
              size='small'
              sx={{ width: 32, height: 32, minHeight: 32 }}
              icon={<Delete fontSize='small' />}
              textColor='error'
              onClick={() => handleDeleteLineItemClick(row.original)}
              disabled={selectedLineItemsIds.length > 0 || !!rangeEditor.rangeEditSubtotalId}
            />
          </Tooltip>
        </Stack>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 160,
        muiTableHeadCellProps: {
          align: 'center' as const,
          sx: {
            px: 1,
            py: 0, // match body padding
            top: 0,
            backgroundColor: 'primary.main',
            color: '#ffffff',
            justifyContent: 'center',
            padding: '12px 9px',
            height: '50px',
            border: 'none',
            position: 'sticky',
            zIndex: 10, // Higher z-index for pinned actions column
            '&[data-pinned="true"]::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'primary.main',
              zIndex: 0,
            },
            '&[data-pinned="true"]': {
              zIndex: 10,
            },
          },
        },
        Header: ({ table }: { table: MRT_TableInstance<ProjectLineItemDTO> }) => (
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
            {/* Add dummy WhiteFab if merge buttons exist to align header with rows */}
            {hasFuzzyMatchedRows && (
              <WhiteFab
                size='small'
                sx={{ width: 32, height: 32, minHeight: 32, opacity: 0, visibility: 'hidden', '&:hover': { opacity: 0 } }}
                icon={<Delete fontSize='small' />}
                textColor='error'
                disabled
              />
            )}
            {/* Add dummy WhiteFab if undo buttons exist to align header with rows */}
            {hasMergedRows && (
              <WhiteFab
                size='small'
                sx={{ width: 32, height: 32, minHeight: 32, opacity: 0, visibility: 'hidden', '&:hover': { opacity: 0 } }}
                icon={<Undo fontSize='small' />}
                textColor='secondary'
                disabled
              />
            )}
            <WhiteFab
              size='small'
              sx={{ width: 32, height: 32, minHeight: 32, opacity: 0, visibility: 'hidden', '&:hover': { opacity: 0 } }}
              icon={<Delete fontSize='small' />}
              textColor='error'
              disabled
            />
            <Tooltip title='Delete Selected Line Items' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Delete fontSize='small' />}
                  textColor='error'
                  onClick={handleBulkDeleteLineItemClick}
                  disabled={Object.keys(rowSelection).length === 0}
                  loading={bulkDeleteInProgress}
                />
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        justifyContent: 'flex-end !important',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0,
        '& > .MuiTablePagination-root': {
          margin: 0,
          justifyContent: 'flex-end',
        },
        '& .css-zrlv9q': {
          justifyContent: 'flex-end !important',
        },
        '& .MuiToolbar-root.MuiToolbar-gutters.css-1613k89-MuiToolbar-root': {
          display: 'none',
        },
        // Hide the column actions button specifically
        '& .MuiButton-root[aria-label*="column"]': {
          display: 'none !important',
        },
        '& .MuiButton-root[aria-label*="Column"]': {
          display: 'none !important',
        },
        '& button[aria-label*="Hide"]': {
          display: 'none !important',
        },
        '& button[aria-label*="Show"]': {
          display: 'none !important',
        },
      },
    },
    renderTopToolbarCustomActions: isFullScreen
      ? () => (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              px: 2,
              py: 0,
            }}
          >
            {renderHeader(true)}
          </Box>
        )
      : undefined,
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<ProjectLineItemDTO> }) => {
      const isSubtotal = row.original?.__rowType === 'subtotal';
      const isRangeChecked = !!rangeEditor.rangeEditSubtotalId && rangeEditor.checkedIds.has(row.original.id);
      const isActiveEditingSubtotal = rangeEditor.rangeEditSubtotalId === row.original.id;

      if (isSubtotal) {
        const subtotalBackgroundColor = SUBTOTAL_ROW_BACKGROUND_COLOR;
        return {
          sx: {
            cursor: 'default',
            backgroundColor: subtotalBackgroundColor + ' !important',
            border: isActiveEditingSubtotal ? '2px solid #1976d2' : 'none',
            fontWeight: 700,
            minHeight: '45px',
            '& td': { minHeight: '45px', paddingTop: 0, paddingBottom: 0, paddingLeft: '6px', paddingRight: '6px' },
            '& td[data-pinned="true"]::before': {
              backgroundColor: subtotalBackgroundColor,
            },
            '& td[data-pinned="true"][data-column-id="mrt-row-actions"]': {
              textAlign: 'center !important',
              '& .MuiStack-root': {
                justifyContent: 'center !important',
              },
            },
          },
        };
      }

      let backgroundColor = '';

      if (isRangeChecked) {
        // Highlights the line items currently claimed by the subtotal being range-edited.
        backgroundColor = '#DCEEFB';
      } else if (row.original?.matchType === 'fuzzy') {
        if (row.original?.isCreated) {
          backgroundColor = REPORT_COLORS.PAYAPP_ONLY_BACKGROUND_COLOR;
        } else if (row.original?.exitingAndNotMatched) {
          backgroundColor = REPORT_COLORS.PROJECT_ONLY_BACKGROUND_COLOR;
        }
      } else if (row.original?.exitingAndNotMatched) {
        backgroundColor = REPORT_COLORS.PROJECT_ONLY_BACKGROUND_COLOR;
      } else if (row.original?.isCreated) {
        backgroundColor = REPORT_COLORS.PAYAPP_ONLY_BACKGROUND_COLOR;
      }

      // Fallback to zebra striping if no custom background
      if (!backgroundColor) {
        backgroundColor = row.index % 2 === 0 ? '#ffffff' : '#ffffff';
      }
      // The fuzzy pair is only adjacent in the default order, so its outline is dropped while sorted.
      const showFuzzyPairOutline = row.original?.matchType === 'fuzzy' && !isSorted;

      return {
        onClick: () => handleRowClick(row),
        sx: {
          cursor: 'pointer',
          backgroundColor: backgroundColor + ' !important',
          border: 'none',
          minHeight: '45px',
          '& td': { minHeight: '45px', paddingTop: 0, paddingBottom: 0, paddingLeft: '6px', paddingRight: '6px' },
          // Also apply the same background to sticky pseudo-elements in this row
          '& td[data-pinned="true"]::before': {
            backgroundColor: backgroundColor,
          },
          // Center align the sticky action rows
          '& td[data-pinned="true"][data-column-id="mrt-row-actions"]': {
            textAlign: 'center !important',
            '& .MuiStack-root': {
              justifyContent: 'center !important',
            },
          },
          // Add outline for mergable pairs (fuzzy-matched rows)
          ...(showFuzzyPairOutline && {
            borderLeft: '2px solid #374151',
            borderRight: '2px solid #374151',
            borderRadius: row.original?.isCreated ? '0.5rem 0.5rem 0 0' : '0 0 0.5rem 0.5rem',
            // Add top border for first row in group
            ...(row.original?.isCreated && {
              borderTop: '2px solid #374151',
            }),
            // Add bottom border for last row in group
            ...(row.original?.exitingAndNotMatched && {
              borderBottom: '2px solid #374151',
            }),
          }),
        },
      };
    },
    state: {
      isFullScreen,
      rowSelection: rangeEditor.rangeEditSubtotalId ? rangeEditSelectionState : rowSelection,
      columnVisibility: columnVisibilityModel,
      sorting,
      showGlobalFilter: false,
    },
    muiTableHeadProps: {
      sx: {
        padding: 0,
        color: 'white',

        position: 'sticky',
        top: 0,
        zIndex: 3,
      },
    },
    muiTableHeadCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        height: '50px',
        padding: '6px 6px',
        fontSize: '0.875rem',
        fontWeight: 'normal',
        border: 'none',
        whiteSpace: 'normal',
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme => theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          flexWrap: 'wrap',
          whiteSpace: 'normal',
        },
        // Hides the sort-direction arrow, matching ProjectTable.
        '& .Mui-TableHeadCell-Content-Labels .MuiBadge-root': { display: 'none' },
      }),
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        padding: '2px 6px',
        '&[data-pinned="true"]': {
          backgroundColor: 'inherit',
        },
      },
    },
    muiTableFooterCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        fontWeight: 'bold',
        padding: '8px 9px',
        border: 'none',
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        '&[data-pinned="true"]::before': {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
      }),
    },
  });
  apiRef.current = table;

  return (
    <Card elevation={0} sx={{ marginTop: 2, marginBottom: 2 }}>
      {/* Header row - show only when not in fullscreen */}
      {!isFullScreen && renderHeader(false)}
      {((!readonly && hasUnresolvedRows) || showSortedSubtotalsNotice) && (
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
          {showSortedSubtotalsNotice && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 'auto', color: 'info.main' }}>
              <InfoOutlined fontSize='small' />
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Subtotals are hidden while the table is sorted. Clear the column sort to see them again.
              </Typography>
            </Box>
          )}
          {!readonly && hasUnresolvedRows && (
            <ToggleButtonGroup
              value={matchMode}
              exclusive
              onChange={handleMatchModeChange}
              size='small'
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid',
                  borderColor: 'divider',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  fontWeight: 500,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  },
                  '&:first-of-type': { borderRadius: '6px 0 0 6px' },
                  '&:last-of-type': { borderRadius: '0 6px 6px 0' },
                },
              }}
            >
              <ToggleButton value='description'>Match by Description</ToggleButton>
              <ToggleButton value='budget'>Match by Budget</ToggleButton>
              <ToggleButton value='previousApplication'>Match by Previous Application</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      )}
      <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
        <MaterialReactTable table={table} />
      </Box>

      {/* Filter Menu */}
      <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}>
        <FilterPanel columns={filterColumns} filterModel={filterModel} setFilterModel={setFilterModel} skippedFields={skippedFields} />
      </Menu>

      {/* Custom Manage Columns Menu */}
      <CustomManageColumnsMenu anchorEl={columnsMenuAnchorEl} setAnchorEl={setColumnsMenuAnchorEl} table={table} title='Manage Columns' />

      <EditPayappDrawlineitemDialog
        open={editDrawLineItemDialogOpen}
        onClose={() => setEditDrawLineItemDialogOpen(false)}
        projectDrawId={projectDraw.projectDrawSummary?.id}
        lineItem={selectedLineItem}
        setValues={handleCreateOrEditLineItem}
      />
    </Card>
  );
}

export default DrawLineItemsTable;
